// src/banner/components/Banner.tsx
import { Button, Card, InlineGrid, Text } from "@shopify/polaris";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// src/shared/types/apps.ts
var FORDEER_APPS = {
  Invoice: "invoice",
  Label: "label",
  Popup: "popup",
  ReelPik: "reelpik"
};
var FordeerApp = FORDEER_APPS;

// src/banner/types/index.ts
var BannerLocation = {
  Dashboard: "dashboard",
  Pricing: "pricing",
  Settings: "settings",
  LabelList: "label-list",
  PopupList: "popup-list",
  LabelOnly: "label-only"
};
var BannerCountry = {
  All: "ALL",
  UnitedStates: "US",
  Netherlands: "NL",
  France: "FR",
  India: "IN",
  UnitedKingdom: "GB",
  Germany: "DE",
  Australia: "AU",
  Canada: "CA"
};

// src/banner/utils/countries.ts
var COUNTRY_NAME_TO_CODE = {
  unitedstates: BannerCountry.UnitedStates,
  usa: BannerCountry.UnitedStates,
  us: BannerCountry.UnitedStates,
  netherlands: BannerCountry.Netherlands,
  holland: BannerCountry.Netherlands,
  nl: BannerCountry.Netherlands,
  france: BannerCountry.France,
  fr: BannerCountry.France,
  india: BannerCountry.India,
  in: BannerCountry.India,
  unitedkingdom: BannerCountry.UnitedKingdom,
  uk: BannerCountry.UnitedKingdom,
  greatbritain: BannerCountry.UnitedKingdom,
  britain: BannerCountry.UnitedKingdom,
  england: BannerCountry.UnitedKingdom,
  gb: BannerCountry.UnitedKingdom,
  germany: BannerCountry.Germany,
  deutschland: BannerCountry.Germany,
  de: BannerCountry.Germany,
  australia: BannerCountry.Australia,
  au: BannerCountry.Australia,
  canada: BannerCountry.Canada,
  ca: BannerCountry.Canada,
  all: BannerCountry.All
};
var normalizeCountryKey = (value) => value?.trim().toLowerCase().replace(/[^a-z]/g, "") ?? "";
var normalizeBannerCountry = (country) => {
  if (!country) return null;
  return COUNTRY_NAME_TO_CODE[normalizeCountryKey(country)] ?? null;
};
var isBannerVisibleForCountry = (merchantCountry, bannerCountries = []) => {
  if (bannerCountries.includes(BannerCountry.All)) {
    return true;
  }
  const normalizedMerchantCountry = normalizeBannerCountry(merchantCountry);
  if (!normalizedMerchantCountry) return false;
  return bannerCountries.includes(normalizedMerchantCountry);
};

// src/shared/utils/contentful.ts
import {
  normalizeContentfulValue,
  normalizeAppIdentifier
} from "@fordeer/contentful-utils";
var getStringFieldValues = (fields) => {
  if (!fields) return [];
  return Object.values(fields).filter(
    (value) => typeof value === "string" && value.trim().length > 0
  );
};
var COUNTRY_FLAG_MAP = {
  "australia": "AU",
  "austria": "AT",
  "belgium": "BE",
  "brazil": "BR",
  "canada": "CA",
  "denmark": "DK",
  "finland": "FI",
  "france": "FR",
  "germany": "DE",
  "india": "IN",
  "ireland": "IE",
  "italy": "IT",
  "japan": "JP",
  "mexico": "MX",
  "netherlands": "NL",
  "new zealand": "NZ",
  "norway": "NO",
  "poland": "PL",
  "portugal": "PT",
  "singapore": "SG",
  "south korea": "KR",
  "spain": "ES",
  "sweden": "SE",
  "switzerland": "CH",
  "united kingdom": "GB",
  "united states": "US"
};
var codeToFlag = (code) => String.fromCodePoint(...[...code.toUpperCase()].map((char) => char.charCodeAt(0) - 65 + 127462));
var countryNameToFlag = (name) => {
  if (!name) return void 0;
  const code = COUNTRY_FLAG_MAP[name.toLowerCase().trim()];
  if (!code) return void 0;
  return codeToFlag(code);
};
var toContentfulAssetUrl = (asset) => {
  const url = asset?.fields?.file?.url;
  if (!url) return void 0;
  return url.startsWith("http") ? url : `https:${url}`;
};
async function getMappedContentEntry({
  app,
  location,
  contentMap,
  getEntry,
  normalizeFields,
  entityName
}) {
  const entryId = contentMap[app]?.[location];
  if (!entryId) return null;
  try {
    const entry = await getEntry(entryId);
    if (!entry?.fields) return null;
    return normalizeFields(entry.fields);
  } catch (error) {
    console.error(`Error fetching ${entityName}:`, error);
    return null;
  }
}

// src/banner/utils/contentful.ts
import { isAfter, isBefore, parseISO } from "date-fns";
var getAppSlug = (app) => normalizeAppIdentifier(app);
var getLinkedAppSlug = (entry) => normalizeAppIdentifier(entry?.fields?.slug);
var getLocationKey = (location) => normalizeContentfulValue(location);
var getLinkedSectionId = (entry) => normalizeContentfulValue(entry?.fields?.sectionId);
var matchesApp = (app, linkedApp) => Boolean(linkedApp) && getAppSlug(app) === getLinkedAppSlug(linkedApp);
var matchesLocation = (location, linkedSection) => Boolean(linkedSection) && getLocationKey(location) === getLinkedSectionId(linkedSection);
var isBannerActiveNow = (banner) => {
  const now = /* @__PURE__ */ new Date();
  const start = parseISO(banner.startDate);
  const end = parseISO(banner.endDate);
  return banner.visibility && isBefore(start, now) && isAfter(end, now);
};
var DEFAULT_BANNER_PRIORITY = 100;
var getBannerPriority = (fields) => fields.priority ?? DEFAULT_BANNER_PRIORITY;
var getMatchingBanners = (collection, app, location) => collection.items.reduce((matchingBanners, entry) => {
  const fields = entry.fields;
  if (!fields) return matchingBanners;
  if (matchesApp(app, fields.app) && matchesLocation(location, fields.appSection)) {
    matchingBanners.push(fields);
  }
  return matchingBanners;
}, []).sort((left, right) => getBannerPriority(left) - getBannerPriority(right));
async function getActiveBanners(app, location, getBanners, merchantCountry) {
  try {
    const collection = await getBanners();
    if (!collection?.items.length) return [];
    return getMatchingBanners(collection, app, location).map((fields) => normalizeBannerData(fields)).filter(
      (banner) => isBannerVisibleForCountry(merchantCountry, banner.displayByCountry) && isBannerActiveNow(banner)
    );
  } catch (error) {
    console.error("Error fetching banner entries:", error);
    return [];
  }
}
async function getActiveBanner(app, location, getBanners, merchantCountry) {
  const activeBanners = await getActiveBanners(
    app,
    location,
    getBanners,
    merchantCountry
  );
  return activeBanners[0] ?? null;
}
var normalizeBannerData = (fields) => ({
  title: fields.title,
  headline: fields.headline,
  bodyText: fields.bodyText,
  promoText: fields.promoText ?? void 0,
  headlineTextColor: fields.headlineTextColor?.value ?? "#FFFFFF",
  bodyTextColor: fields.bodyTextColor?.value ?? "#FFFFFF",
  promoTextColor: fields.promoTextColor?.value ?? "#FFFFFF",
  foregroundImage: toContentfulAssetUrl(fields.foregroundImage),
  backgroundImage: toContentfulAssetUrl(fields.backgroundImage),
  backgroundColor: fields.backgroundColor?.value ?? void 0,
  ctaLabel: fields.ctaLabel,
  ctaLinkType: fields.ctaLinkType ?? void 0,
  ctaInternalRoute: fields.ctaInternalRoute ?? void 0,
  ctaExternalUrl: fields.ctaExternalUrl ?? void 0,
  displayByCountry: (fields.displayByCountry?.length ? fields.displayByCountry : [BannerCountry.All]) ?? [BannerCountry.All],
  visibility: fields.visibility,
  startDate: fields.startDate,
  endDate: fields.endDate,
  priority: fields.priority ?? DEFAULT_BANNER_PRIORITY
});

// src/banner/components/Banner.tsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";

// #style-inject:#style-inject
function styleInject(css, { insertAt } = {}) {
  if (!css || typeof document === "undefined") return;
  const head = document.head || document.getElementsByTagName("head")[0];
  const style = document.createElement("style");
  style.type = "text/css";
  if (insertAt === "top") {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

// src/banner/components/Banner.css
styleInject(".banner {\n  position: relative;\n  color: white;\n  min-height: 210px;\n}\n.banner__text {\n  padding: 20px;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n  justify-content: space-between;\n  align-items: start;\n}\n.banner__image {\n  position: relative;\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  object-fit: cover;\n  transition: 0.13s;\n  overflow: hidden;\n}\n.banner__close-button {\n  position: absolute;\n  top: 12px;\n  right: 12px;\n  z-index: 12;\n  display: flex;\n  padding: 0;\n  border: 0;\n  background: none;\n  cursor: pointer;\n}\n.banner__carousel {\n  position: relative;\n  --banner-slide-size: 72%;\n  --banner-slide-gap: 16px;\n}\n.banner__viewport {\n  overflow: hidden;\n  padding: 16px 16px 0;\n}\n.banner__track {\n  display: flex;\n  gap: var(--banner-slide-gap);\n  align-items: stretch;\n  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);\n  will-change: transform;\n}\n.banner__slide {\n  flex: 0 0 var(--banner-slide-size);\n  display: flex;\n  overflow: hidden;\n  border: 1px solid #d4d4d4;\n  border-radius: 12px;\n  box-shadow:\n    1px 0 0 0 rgba(0, 0, 0, 0.13) inset,\n    -1px 0 0 0 rgba(0, 0, 0, 0.13) inset,\n    0 -1px 0 0 rgba(0, 0, 0, 0.17) inset,\n    0 1px 0 0 rgba(204, 204, 204, 0.5) inset,\n    0 1px 0 0 rgba(26, 26, 26, 0.07);\n}\n.banner__slide--last {\n  flex-basis: 100%;\n}\n.banner__slide .banner,\n.banner__slide .banner__grid,\n.banner__slide .banner__grid > .Polaris-InlineGrid {\n  height: 100%;\n  width: 100%;\n}\n@media (max-width: 767px) {\n  .banner__image {\n    display: none !important;\n  }\n  .banner__grid {\n    gap: 0 !important;\n  }\n  .banner__viewport {\n    padding: 0;\n  }\n  .banner__carousel {\n    --banner-slide-size: 100%;\n    --banner-slide-gap: 0px;\n  }\n  .banner__slide {\n    border-radius: 0;\n  }\n}\n.banner__carousel-controls {\n  display: flex;\n  align-items: center;\n  margin-top: 6px;\n  gap: 12px;\n  padding: 6px 16px 12px;\n}\n.banner__nav-group {\n  display: inline-flex;\n  align-items: center;\n}\n.banner__nav-button {\n  border: 0;\n  background: rgba(0, 0, 0, 0.04);\n  color: #3f3f46;\n  width: 28px;\n  height: 28px;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 4px;\n}\n.banner__nav-button--previous {\n  border-radius: 8px 0 0 8px;\n}\n.banner__nav-button--next {\n  border-radius: 0 8px 8px 0;\n}\n.banner__nav-button:hover {\n  background: var(--p-color-bg-fill-tertiary, #e4e4e7);\n}\n.banner__nav-button:disabled {\n  color: #3f3f46;\n  cursor: default;\n  background: rgba(0, 0, 0, 0.04);\n  opacity: 0.5;\n}\n.banner__counter {\n  color: #303030;\n  font-family: Inter, sans-serif;\n  font-size: 13px;\n  line-height: 20px;\n  min-width: 33px;\n}\n");

// src/banner/components/Banner.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var readReservedHeight = (key, width) => {
  if (typeof window === "undefined" || !width) return 0;
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return 0;
    const parsed = JSON.parse(saved);
    if (typeof parsed?.width !== "number" || typeof parsed?.height !== "number")
      return 0;
    return Math.abs(parsed.width - width) <= 1 ? parsed.height : 0;
  } catch {
    return 0;
  }
};
var writeReservedHeight = (key, size) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(size));
  } catch {
  }
};
var Banner = ({
  app,
  location,
  getBanners,
  merchantCountry,
  onBannerClosed,
  onButtonClicked,
  onInternalRouteNavigate,
  reserveSpaceKey
}) => {
  const [banners, setBanners] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [reservedHeight, setReservedHeight] = useState(null);
  const containerRef = useRef(null);
  const measuredWidthRef = useRef(0);
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const fetchedBanners = await getActiveBanners(
          app,
          location,
          getBanners,
          merchantCountry
        );
        setBanners(fetchedBanners);
        setCurrentIndex(0);
      } catch (error) {
        console.error("Error fetching banner:", error);
      } finally {
        setIsLoaded(true);
      }
    };
    fetchBanner();
  }, [app, location, getBanners, merchantCountry]);
  const isReserving = Boolean(reserveSpaceKey) && !isLoaded;
  const hasBanners = banners.length > 0 && isVisible;
  useLayoutEffect(() => {
    if (!reserveSpaceKey || !isReserving || reservedHeight !== null) return;
    const width = containerRef.current?.getBoundingClientRect().width ?? 0;
    measuredWidthRef.current = width;
    setReservedHeight(readReservedHeight(reserveSpaceKey, width));
  }, [isReserving, reserveSpaceKey, reservedHeight]);
  useEffect(() => {
    if (!reserveSpaceKey || !isLoaded) return;
    const container = containerRef.current;
    if (!hasBanners || !container) {
      const width = measuredWidthRef.current;
      if (width) writeReservedHeight(reserveSpaceKey, { width, height: 0 });
      return;
    }
    const store = () => {
      const { width, height } = container.getBoundingClientRect();
      if (width) writeReservedHeight(reserveSpaceKey, { width, height });
    };
    store();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(store);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasBanners, isLoaded, reserveSpaceKey]);
  if (isReserving) {
    if (reservedHeight === 0) return null;
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref: containerRef,
        "aria-hidden": "true",
        style: reservedHeight ? { height: reservedHeight } : void 0
      }
    );
  }
  if (!hasBanners) return null;
  const withSizeTracking = (node) => reserveSpaceKey ? /* @__PURE__ */ jsx("div", { ref: containerRef, children: node }) : node;
  const handleBannerClick = (banner) => {
    onButtonClicked?.();
    if (banner.ctaLinkType === true && banner.ctaInternalRoute) {
      if (onInternalRouteNavigate) {
        onInternalRouteNavigate(banner.ctaInternalRoute);
        return;
      }
      window.location.assign(banner.ctaInternalRoute);
      return;
    }
    if (banner.ctaExternalUrl) {
      window.open(banner.ctaExternalUrl, "_blank", "noopener,noreferrer");
    }
  };
  const handleClose = () => {
    setIsVisible(false);
    onBannerClosed?.();
  };
  const hasMultipleBanners = banners.length > 1;
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < banners.length - 1;
  const handlePrevious = () => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  };
  const handleNext = () => {
    setCurrentIndex((index) => Math.min(banners.length - 1, index + 1));
  };
  const renderBanner = (banner) => {
    const isInternalRoute = banner.ctaLinkType === true;
    const hasInternalRoute = Boolean(banner.ctaInternalRoute);
    const hasExternalUrl = Boolean(banner.ctaExternalUrl);
    const canTriggerCta = isInternalRoute && hasInternalRoute || !isInternalRoute && hasExternalUrl;
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "banner banner__grid",
        style: banner.backgroundColor ? { backgroundColor: banner.backgroundColor } : void 0,
        children: /* @__PURE__ */ jsxs(InlineGrid, { gap: "400", columns: { xs: 1, md: 2 }, children: [
          /* @__PURE__ */ jsxs("div", { className: "banner__text", children: [
            /* @__PURE__ */ jsx("div", { style: { color: banner.headlineTextColor }, children: /* @__PURE__ */ jsx(Text, { as: "h2", variant: "headingMd", children: banner.headline }) }),
            /* @__PURE__ */ jsx("div", { style: { color: banner.bodyTextColor }, children: /* @__PURE__ */ jsx(Text, { as: "p", alignment: "start", variant: "bodyMd", children: banner.bodyText }) }),
            banner.promoText && /* @__PURE__ */ jsx("div", { style: { color: banner.promoTextColor }, children: /* @__PURE__ */ jsx(Text, { as: "p", variant: "headingMd", children: banner.promoText }) }),
            /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
              Button,
              {
                onClick: () => handleBannerClick(banner),
                disabled: !canTriggerCta,
                children: banner.ctaLabel
              }
            ) })
          ] }),
          banner.foregroundImage && /* @__PURE__ */ jsx("div", { className: "banner__image", children: /* @__PURE__ */ jsx(
            "img",
            {
              src: banner.foregroundImage,
              alt: "banner",
              width: "365",
              height: "210"
            }
          ) })
        ] })
      }
    );
  };
  if (!hasMultipleBanners) {
    return withSizeTracking(
      /* @__PURE__ */ jsxs(Card, { padding: "0", roundedAbove: "xs", children: [
        renderBanner(banners[0]),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "banner__close-button",
            onClick: handleClose,
            "aria-label": "Close banner",
            children: /* @__PURE__ */ jsx(X, { size: 20, color: "#ada8a8" })
          }
        )
      ] })
    );
  }
  return withSizeTracking(
    /* @__PURE__ */ jsxs(Card, { padding: "0", roundedAbove: "xs", children: [
      /* @__PURE__ */ jsxs("div", { className: "banner__carousel", children: [
        /* @__PURE__ */ jsx("div", { className: "banner__viewport", children: /* @__PURE__ */ jsx(
          "div",
          {
            className: "banner__track",
            style: {
              transform: `translateX(calc(${currentIndex} * -1 * (var(--banner-slide-size) + var(--banner-slide-gap))))`
            },
            children: banners.map((banner, index) => {
              const isLastBanner = index === banners.length - 1;
              return /* @__PURE__ */ jsx(
                "div",
                {
                  className: `banner__slide${isLastBanner ? " banner__slide--last" : ""}`,
                  children: renderBanner(banner)
                },
                `${banner.title}-${banner.priority}-${index}`
              );
            })
          }
        ) }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "banner__close-button",
            onClick: handleClose,
            "aria-label": "Close banner",
            children: /* @__PURE__ */ jsx(X, { size: 20, color: "#ada8a8" })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "banner__carousel-controls", children: [
        /* @__PURE__ */ jsxs("div", { className: "banner__nav-group", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "banner__nav-button banner__nav-button--previous",
              onClick: handlePrevious,
              disabled: !canGoPrevious,
              "aria-label": "Show previous banner",
              children: /* @__PURE__ */ jsx(ChevronLeft, { size: 20 })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "banner__nav-button banner__nav-button--next",
              onClick: handleNext,
              disabled: !canGoNext,
              "aria-label": "Show next banner",
              children: /* @__PURE__ */ jsx(ChevronRight, { size: 20 })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "banner__counter", children: [
          currentIndex + 1,
          " of ",
          banners.length
        ] })
      ] })
    ] })
  );
};

// src/shared/components/Marquee.css
styleInject('.fordeer-marquee {\n  position: relative;\n  overflow: hidden;\n  width: 100%;\n}\n.fordeer-marquee::before,\n.fordeer-marquee::after {\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  z-index: 1;\n  width: 48px;\n  content: "";\n  pointer-events: none;\n}\n.fordeer-marquee::before {\n  left: 0;\n  background:\n    linear-gradient(\n      90deg,\n      rgba(255, 255, 255, 1),\n      rgba(255, 255, 255, 0));\n}\n.fordeer-marquee::after {\n  right: 0;\n  background:\n    linear-gradient(\n      270deg,\n      rgba(255, 255, 255, 1),\n      rgba(255, 255, 255, 0));\n}\n.fordeer-marquee__track {\n  display: flex;\n  width: max-content;\n  will-change: transform;\n  animation: fordeer-marquee-scroll var(--marquee-speed, 40s) linear infinite;\n}\n.fordeer-marquee--pause-on-hover:hover .fordeer-marquee__track {\n  animation-play-state: paused;\n}\n.fordeer-marquee__set {\n  display: flex;\n  gap: var(--marquee-gap, 20px);\n  padding-right: var(--marquee-gap, 20px);\n}\n@keyframes fordeer-marquee-scroll {\n  from {\n    transform: translateX(0);\n  }\n  to {\n    transform: translateX(-50%);\n  }\n}\n@media (max-width: 640px) {\n  .fordeer-marquee::before,\n  .fordeer-marquee::after {\n    width: 24px;\n  }\n}\n');

// src/shared/components/Marquee.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Marquee = ({
  children,
  speed = 40,
  pauseOnHover = true,
  gap = 16,
  ariaLabel
}) => {
  const trackStyle = {
    animationPlayState: "running",
    "--marquee-speed": `${speed}s`,
    "--marquee-gap": `${gap}px`
  };
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: `fordeer-marquee${pauseOnHover ? " fordeer-marquee--pause-on-hover" : ""}`,
      role: "marquee",
      "aria-label": ariaLabel,
      children: /* @__PURE__ */ jsxs2("div", { className: "fordeer-marquee__track", style: trackStyle, children: [
        /* @__PURE__ */ jsx2("div", { className: "fordeer-marquee__set", "aria-hidden": false, children }),
        /* @__PURE__ */ jsx2("div", { className: "fordeer-marquee__set", "aria-hidden": true, children })
      ] })
    }
  );
};

// src/faq/components/FaqAccordion.tsx
import {
  BlockStack,
  Card as Card2,
  Collapsible,
  InlineStack,
  Link,
  SkeletonBodyText,
  Text as Text2
} from "@shopify/polaris";
import { ChevronDown } from "lucide-react";
import { useId, useState as useState2 } from "react";

// src/faq/components/FaqAccordion.css
styleInject(".faqAccordion {\n  width: 100%;\n}\n.faqAccordion__header {\n  margin-bottom: 16px;\n}\n.faqAccordion__list {\n  border: 1px solid #dfe3e8;\n  border-radius: 12px;\n  overflow: hidden;\n  background: #fff;\n}\n.faqAccordion__item {\n  border-bottom: 1px solid #dfe3e8;\n}\n.faqAccordion__item:last-child {\n  border-bottom: none;\n}\n.faqAccordion__trigger {\n  width: 100%;\n  cursor: pointer;\n  padding: 16px 20px;\n  background: #fff;\n}\n.faqAccordion__trigger:hover {\n  background: #f6f6f7;\n}\n.faqAccordion__answer {\n  padding: 20px 20px;\n  background: #fff;\n}\n.faqAccordion__richList {\n  margin: 0;\n  padding-left: 20px;\n}\n.faqAccordion__richList li + li {\n  margin-top: 8px;\n}\n.faqAccordion__icon {\n  transition: transform 0.2s ease;\n}\n.faqAccordion__icon--expanded {\n  transform: rotate(180deg);\n}\n");

// src/faq/components/FaqAccordion.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var FaqAccordion = ({
  items,
  title = "Frequently asked questions",
  description = "Answers to common questions.",
  loading = false,
  error,
  defaultOpenItemId
}) => {
  const [openFaqId, setOpenFaqId] = useState2(
    defaultOpenItemId ?? items[0]?.id ?? null
  );
  if (!loading && !error && !items.length) {
    return null;
  }
  return /* @__PURE__ */ jsx3("div", { className: "faqAccordion", children: /* @__PURE__ */ jsx3(Card2, { roundedAbove: "xs", children: /* @__PURE__ */ jsxs3(BlockStack, { gap: "400", children: [
    /* @__PURE__ */ jsxs3("div", { className: "faqAccordion__header", children: [
      /* @__PURE__ */ jsx3(Text2, { as: "h2", variant: "headingMd", children: title }),
      /* @__PURE__ */ jsx3(Text2, { as: "p", tone: "subdued", variant: "bodyMd", children: description })
    ] }),
    loading ? /* @__PURE__ */ jsx3(SkeletonBodyText, { lines: 4 }) : error ? /* @__PURE__ */ jsx3(Text2, { as: "p", tone: "critical", variant: "bodyMd", children: error }) : /* @__PURE__ */ jsx3("div", { className: "faqAccordion__list", children: items.map((item) => /* @__PURE__ */ jsx3(
      FaqAccordionItem,
      {
        item,
        open: openFaqId === item.id,
        onToggle: () => setOpenFaqId(
          (currentOpenFaqId) => currentOpenFaqId === item.id ? null : item.id
        )
      },
      item.id
    )) })
  ] }) }) });
};
var FaqAccordionItem = ({
  item,
  open,
  onToggle
}) => {
  const collapsibleId = useId();
  return /* @__PURE__ */ jsxs3("div", { className: "faqAccordion__item", children: [
    /* @__PURE__ */ jsx3(
      "button",
      {
        type: "button",
        className: "faqAccordion__trigger",
        onClick: onToggle,
        "aria-expanded": open,
        "aria-controls": collapsibleId,
        children: /* @__PURE__ */ jsxs3(InlineStack, { align: "space-between", blockAlign: "center", gap: "300", children: [
          /* @__PURE__ */ jsx3(Text2, { as: "span", variant: "headingMd", fontWeight: "semibold", children: item.question }),
          /* @__PURE__ */ jsx3(
            "div",
            {
              className: `faqAccordion__icon ${open ? "faqAccordion__icon--expanded" : ""}`,
              children: /* @__PURE__ */ jsx3(ChevronDown, { size: 20, color: "#6d7175", "aria-hidden": "true" })
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx3(
      Collapsible,
      {
        id: collapsibleId,
        open,
        transition: { duration: "200ms", timingFunction: "ease-in-out" },
        expandOnPrint: true,
        children: /* @__PURE__ */ jsx3("div", { className: "faqAccordion__answer", children: /* @__PURE__ */ jsx3(BlockStack, { gap: "300", children: item.answer.map((block, index) => /* @__PURE__ */ jsx3(
          FaqAnswerBlockContent,
          {
            block
          },
          `${item.id}-${block.type}-${index}`
        )) }) })
      }
    )
  ] });
};
var FaqAnswerBlockContent = ({ block }) => {
  if (block.type === "list") {
    const ListTag = block.ordered ? "ol" : "ul";
    return /* @__PURE__ */ jsx3(ListTag, { className: "faqAccordion__richList", children: block.items.map((item, index) => /* @__PURE__ */ jsx3("li", { children: /* @__PURE__ */ jsx3(Text2, { as: "span", variant: "bodyMd", children: /* @__PURE__ */ jsx3(FaqInlineContent, { nodes: item }) }) }, `${block.type}-${index}`)) });
  }
  return /* @__PURE__ */ jsx3(Text2, { as: "p", variant: "bodyMd", children: /* @__PURE__ */ jsx3(FaqInlineContent, { nodes: block.children }) });
};
var FaqInlineContent = ({ nodes }) => /* @__PURE__ */ jsx3(Fragment, { children: nodes.map((node, index) => {
  if (node.type === "link") {
    return /* @__PURE__ */ jsx3(
      Link,
      {
        url: node.href,
        target: "_blank",
        removeUnderline: false,
        children: /* @__PURE__ */ jsx3(FaqInlineContent, { nodes: node.children })
      },
      `link-${node.href}-${index}`
    );
  }
  let content = node.text;
  if (node.bold) {
    content = /* @__PURE__ */ jsx3("strong", { children: content });
  }
  if (node.italic) {
    content = /* @__PURE__ */ jsx3("em", { children: content });
  }
  return /* @__PURE__ */ jsx3("span", { children: content }, `text-${index}`);
}) });

// src/faq/constants/index.ts
var FORDEER_APP_SECTIONS = {
  PRICING: "pricing"
};

// src/faq/utils/contentful.ts
var matchesApp2 = (appSlug, linkedApp) => {
  const normalizedAppSlug = normalizeAppIdentifier(appSlug);
  const linkedValues = getStringFieldValues(linkedApp?.fields);
  return linkedValues.some(
    (value) => normalizeAppIdentifier(value) === normalizedAppSlug
  );
};
var matchesSection = (sectionSlug, linkedSection) => {
  const sectionSlugs = Array.isArray(sectionSlug) ? sectionSlug : [sectionSlug];
  const normalizedSectionSlugs = sectionSlugs.map(normalizeContentfulValue);
  const linkedValues = getStringFieldValues(linkedSection?.fields);
  return linkedValues.some(
    (value) => normalizedSectionSlugs.includes(normalizeContentfulValue(value))
  );
};
var normalizeInlineNodes = (nodes) => nodes?.flatMap((node) => {
  if (node.nodeType === "text") {
    const text = node.value ?? "";
    if (!text) return [];
    return [
      {
        type: "text",
        text,
        bold: node.marks?.some((mark) => mark.type === "bold") ?? false,
        italic: node.marks?.some((mark) => mark.type === "italic") ?? false
      }
    ];
  }
  if (node.nodeType === "hyperlink") {
    const href = node.data?.uri;
    const children = normalizeInlineNodes(node.content);
    if (!href || !children.length) {
      return children;
    }
    return [
      {
        type: "link",
        href,
        children
      }
    ];
  }
  return normalizeInlineNodes(node.content);
}) ?? [];
var normalizeFaqAnswer = (document2) => {
  if (!document2?.content?.length) {
    return [];
  }
  const blocks = [];
  document2.content.forEach((node) => {
    if (node.nodeType === "unordered-list" || node.nodeType === "ordered-list") {
      const items = node.content?.map((listItem) => normalizeInlineNodes(listItem.content)).filter((item) => item.some(
        (inlineNode) => inlineNode.type === "text" ? inlineNode.text.trim().length > 0 : inlineNode.children.length > 0
      )) ?? [];
      if (items.length) {
        blocks.push({
          type: "list",
          ordered: node.nodeType === "ordered-list",
          items
        });
      }
      return;
    }
    const children = normalizeInlineNodes(node.content);
    const hasVisibleText = children.some(
      (inlineNode) => inlineNode.type === "text" ? inlineNode.text.trim().length > 0 : inlineNode.children.length > 0
    );
    if (hasVisibleText) {
      blocks.push({
        type: "paragraph",
        children
      });
    }
  });
  return blocks;
};
var normalizeFaqItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.reduce((acc, item) => {
    const question = item.fields?.question?.trim();
    const answer = normalizeFaqAnswer(item.fields?.answer);
    if (!question || !answer.length) {
      return acc;
    }
    acc.push({
      id: item.sys.id,
      question,
      answer
    });
    return acc;
  }, []);
};
var getActiveFaqItems = async ({
  appSlug,
  sectionSlug,
  getSections
}) => {
  const collection = await getSections();
  if (!collection?.items.length) return [];
  return collection.items.reduce((acc, entry) => {
    if (!matchesApp2(appSlug, entry.fields?.app)) {
      return acc;
    }
    if (!matchesSection(sectionSlug, entry.fields?.appSection)) {
      return acc;
    }
    return acc.concat(normalizeFaqItems(entry.fields?.items));
  }, []);
};

// src/tutorial/components/TutorialSection.tsx
import { Box, Button as Button2, Card as Card3, Text as Text3, BlockStack as BlockStack2 } from "@shopify/polaris";
import { ChevronLeft as ChevronLeft2, ChevronRight as ChevronRight2, X as X2 } from "lucide-react";

// src/tutorial/types/index.ts
var TutorialLocation = {
  Dashboard: "dashboard",
  GettingStarted: "getting-started",
  Settings: "settings"
};

// src/tutorial/constants/index.ts
var INVOICE_DASHBOARD_TUTORIAL = "4ZGXsIXYn9bEQTqO7KNrBY";
var INVOICE_GETTING_STARTED = "1HbnG0Cbp1C7hpcQiifZGs1";
var LABEL_DASHBOARD_TUTORIAL = "149tXsxhdPQQV9eJiprQOX1";
var LABEL_GETTING_STARTED = "2137PCSN2Fd42Qm8RmeDM11";
var POPUP_DASHBOARD_TUTORIAL = "6N0DCjUK6ZFKyTfZPQsWM21";
var POPUP_GETTING_STARTED = "5rfWlggVpPGT6Se6GALATB1";
var tutorialContentMap = {
  [FordeerApp.Invoice]: {
    [TutorialLocation.Dashboard]: INVOICE_DASHBOARD_TUTORIAL,
    [TutorialLocation.GettingStarted]: INVOICE_GETTING_STARTED
  },
  [FordeerApp.Label]: {
    [TutorialLocation.Dashboard]: LABEL_DASHBOARD_TUTORIAL,
    [TutorialLocation.GettingStarted]: LABEL_GETTING_STARTED
  },
  [FordeerApp.Popup]: {
    [TutorialLocation.Dashboard]: POPUP_DASHBOARD_TUTORIAL,
    [TutorialLocation.GettingStarted]: POPUP_GETTING_STARTED
  },
  [FordeerApp.ReelPik]: {}
};

// src/tutorial/utils/contentful.ts
async function getActiveTutorialSection(app, location, getTutorialSection) {
  return getMappedContentEntry({
    app,
    location,
    contentMap: tutorialContentMap,
    getEntry: getTutorialSection,
    normalizeFields: (fields) => normalizeTutorialSectionData(fields),
    entityName: "tutorial section entry"
  });
}
var normalizeTutorialSectionData = (fields) => {
  const normalizedSlides = fields.slides?.map((slide) => normalizeTutorialSlideData(slide.fields)).sort((a, b) => a.order - b.order) || [];
  return {
    title: fields.title,
    subtitle: fields.subtitle ?? void 0,
    slides: normalizedSlides,
    visibility: fields.visibility,
    startDate: fields.startDate,
    endDate: fields.endDate
  };
};
var normalizeTutorialSlideData = (fields) => ({
  title: fields.title,
  description: fields.description ?? void 0,
  image: toContentfulAssetUrl(fields.image),
  buttonText: fields.buttonText ?? void 0,
  buttonUrl: fields.buttonUrl ?? void 0,
  order: fields.order
});

// src/tutorial/components/TutorialSection.tsx
import { isAfter as isAfter2, isBefore as isBefore2, parseISO as parseISO2 } from "date-fns";
import { useState as useState3, useEffect as useEffect2, useCallback } from "react";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var styles = {
  container: {
    position: "relative",
    padding: "24px",
    backgroundColor: "#f9fafb",
    borderRadius: "12px"
  },
  header: {
    textAlign: "center",
    marginBottom: "24px"
  },
  slideContainer: {
    position: "relative",
    overflow: "hidden"
  },
  slidesWrapper: {
    display: "flex",
    transition: "transform 0.3s ease-in-out"
  },
  slide: {
    minWidth: "100%",
    padding: "0 12px"
  },
  slideCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column"
  },
  slideImage: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: "8px",
    marginBottom: "16px"
  },
  slideContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px"
  },
  navButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #e1e5e9",
    backgroundColor: "white",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  navButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed"
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  paginationDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#e1e5e9",
    cursor: "pointer",
    transition: "background-color 0.2s ease"
  },
  paginationDotActive: {
    backgroundColor: "#005bd3"
  },
  closeButton: {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: 10,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
  }
};
var TutorialSection = ({
  app,
  location,
  getTutorialSection,
  onSlideClick,
  onTutorialClosed,
  showCloseButton = true,
  className
}) => {
  const [tutorialSection, setTutorialSection] = useState3(null);
  const [currentSlide, setCurrentSlide] = useState3(0);
  const [isLoading, setIsLoading] = useState3(true);
  const [isVisible, setIsVisible] = useState3(true);
  const [isClosing, setIsClosing] = useState3(false);
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onTutorialClosed?.();
    }, 200);
  }, [onTutorialClosed]);
  const handleSlideClick = useCallback(
    (slideIndex) => {
      if (!tutorialSection?.slides) return;
      const slide = tutorialSection.slides[slideIndex];
      onSlideClick?.(slideIndex, slide.buttonUrl);
      if (slide.buttonUrl) {
        window.open(slide.buttonUrl, "_blank");
      }
    },
    [onSlideClick, tutorialSection?.slides]
  );
  useEffect2(() => {
    const fetchTutorialSection = async () => {
      try {
        setIsLoading(true);
        const fetchedTutorialSection = await getActiveTutorialSection(
          app,
          location,
          getTutorialSection
        );
        setTutorialSection(fetchedTutorialSection);
      } catch (error) {
        console.error("Error fetching tutorial section:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTutorialSection();
  }, [app, location, getTutorialSection]);
  if (isLoading) {
    return /* @__PURE__ */ jsx4(Card3, { children: /* @__PURE__ */ jsx4(Box, { padding: "600", children: /* @__PURE__ */ jsx4(Text3, { as: "p", alignment: "center", children: "Loading tutorials..." }) }) });
  }
  if (!tutorialSection) return null;
  const { title, subtitle, slides, visibility, startDate, endDate } = tutorialSection;
  const now = /* @__PURE__ */ new Date();
  const start = parseISO2(startDate);
  const end = parseISO2(endDate);
  const shouldShowTutorial = visibility && isBefore2(start, now) && isAfter2(end, now);
  if (!shouldShowTutorial || !slides.length || !isVisible) return null;
  const nextSlide = () => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };
  return /* @__PURE__ */ jsx4(Card3, { children: /* @__PURE__ */ jsxs4(
    "div",
    {
      style: {
        ...styles.container,
        ...isClosing ? {
          animation: "tutorialFadeOut 0.2s ease-out forwards",
          opacity: isClosing ? 0 : 1,
          transform: isClosing ? "translateY(-10px)" : "translateY(0)"
        } : {}
      },
      className,
      children: [
        showCloseButton && /* @__PURE__ */ jsx4(
          "button",
          {
            style: styles.closeButton,
            onClick: handleClose,
            "aria-label": "Close tutorial section",
            type: "button",
            children: /* @__PURE__ */ jsx4(X2, { size: 20, color: "#666" })
          }
        ),
        /* @__PURE__ */ jsx4("div", { style: styles.header, children: /* @__PURE__ */ jsxs4(BlockStack2, { gap: "200", children: [
          /* @__PURE__ */ jsx4(Text3, { as: "h2", variant: "headingLg", alignment: "center", children: title }),
          subtitle && /* @__PURE__ */ jsx4(Text3, { as: "p", variant: "bodyMd", alignment: "center", tone: "subdued", children: subtitle })
        ] }) }),
        /* @__PURE__ */ jsx4("div", { style: styles.slideContainer, children: /* @__PURE__ */ jsx4(
          "div",
          {
            style: {
              ...styles.slidesWrapper,
              transform: `translateX(-${currentSlide * 100}%)`
            },
            children: slides.map((slide, index) => /* @__PURE__ */ jsx4("div", { style: styles.slide, children: /* @__PURE__ */ jsx4(Card3, { children: /* @__PURE__ */ jsx4(Box, { padding: "400", children: /* @__PURE__ */ jsxs4("div", { style: styles.slideContent, children: [
              slide.image && /* @__PURE__ */ jsx4(
                "img",
                {
                  src: slide.image,
                  alt: slide.title,
                  style: styles.slideImage
                }
              ),
              /* @__PURE__ */ jsxs4(BlockStack2, { gap: "300", children: [
                /* @__PURE__ */ jsx4(Text3, { as: "h3", variant: "headingMd", children: slide.title }),
                slide.description && /* @__PURE__ */ jsx4(Text3, { as: "p", variant: "bodyMd", tone: "subdued", children: slide.description }),
                slide.buttonText && /* @__PURE__ */ jsx4(Box, { children: /* @__PURE__ */ jsx4(
                  Button2,
                  {
                    onClick: () => handleSlideClick(index),
                    variant: "primary",
                    children: slide.buttonText
                  }
                ) })
              ] })
            ] }) }) }) }, index))
          }
        ) }),
        slides.length > 1 && /* @__PURE__ */ jsxs4("div", { style: styles.navigation, children: [
          /* @__PURE__ */ jsx4(
            "button",
            {
              style: {
                ...styles.navButton,
                ...currentSlide === 0 ? styles.navButtonDisabled : {}
              },
              onClick: prevSlide,
              disabled: currentSlide === 0,
              "aria-label": "Previous slide",
              children: /* @__PURE__ */ jsx4(ChevronLeft2, { size: 20 })
            }
          ),
          /* @__PURE__ */ jsx4("div", { style: styles.pagination, children: /* @__PURE__ */ jsxs4(Text3, { as: "span", variant: "bodySm", tone: "subdued", children: [
            currentSlide + 1,
            " of ",
            slides.length
          ] }) }),
          /* @__PURE__ */ jsx4(
            "button",
            {
              style: {
                ...styles.navButton,
                ...currentSlide === slides.length - 1 ? styles.navButtonDisabled : {}
              },
              onClick: nextSlide,
              disabled: currentSlide === slides.length - 1,
              "aria-label": "Next slide",
              children: /* @__PURE__ */ jsx4(ChevronRight2, { size: 20 })
            }
          )
        ] })
      ]
    }
  ) });
};

// src/feature-guide/components/FeatureGuide.tsx
import { useEffect as useEffect3, useState as useState4 } from "react";

// src/feature-guide/utils/contentful.ts
import { isAfter as isAfter3, isBefore as isBefore3, parseISO as parseISO3 } from "date-fns";
var getAppSlug2 = (app) => normalizeContentfulValue(app);
var getNestedString = (value, key) => {
  if (!value || typeof value !== "object") return void 0;
  const nestedValue = value[key];
  return typeof nestedValue === "string" ? nestedValue : void 0;
};
var getOptionalString = (value) => typeof value === "string" ? value : void 0;
var getLinkedAppSlug2 = (entry) => normalizeContentfulValue(
  getNestedString(
    entry?.fields.app?.fields,
    "slug"
  )
);
var getLinkedAppSectionId = (entry) => normalizeContentfulValue(
  getNestedString(
    entry?.fields.appSection?.fields,
    "sectionId"
  )
);
var isWithinActiveWindow = (startDate, endDate) => {
  const now = /* @__PURE__ */ new Date();
  if (startDate) {
    const start = parseISO3(startDate);
    if (!Number.isNaN(start.getTime()) && isBefore3(now, start)) {
      return false;
    }
  }
  if (endDate) {
    const end = parseISO3(endDate);
    if (!Number.isNaN(end.getTime()) && isAfter3(now, end)) {
      return false;
    }
  }
  return true;
};
var normalizeFeatureGuideVideoData = (fields, entryId) => ({
  id: entryId,
  internalName: fields.internalName,
  appSlug: getNestedString(
    fields.app?.fields,
    "slug"
  ),
  appSectionId: getNestedString(
    fields.appSection?.fields,
    "sectionId"
  ),
  title: fields.title,
  description: fields.description ?? void 0,
  youtubeUrl: fields.youtubeUrl,
  fallbackUrl: fields.fallbackUrl ?? void 0,
  thumbnail: toContentfulAssetUrl(fields.thumbnail),
  visibility: fields.visibility,
  autoShowEnabled: fields.autoShowEnabled,
  startDate: fields.startDate ?? void 0,
  endDate: fields.endDate ?? void 0
});
async function getActiveFeatureGuide({
  app,
  appSectionId,
  getFeatureGuideVideos
}) {
  try {
    const collection = await getFeatureGuideVideos();
    const items = collection?.items ?? [];
    const normalizedApp = getAppSlug2(app);
    const normalizedSectionId = normalizeContentfulValue(appSectionId);
    const matchedEntry = items.find((entry) => {
      if (!entry.fields.visibility) return false;
      const sameApp = getLinkedAppSlug2(entry) === normalizedApp;
      const sameSection = getLinkedAppSectionId(entry) === normalizedSectionId;
      const activeWindow = isWithinActiveWindow(
        getOptionalString(entry.fields.startDate),
        getOptionalString(entry.fields.endDate)
      );
      return sameApp && sameSection && activeWindow;
    });
    if (!matchedEntry?.fields) return null;
    return normalizeFeatureGuideVideoData(
      matchedEntry.fields,
      matchedEntry.sys.id
    );
  } catch (error) {
    console.error("Error fetching feature guide video:", error);
    return null;
  }
}
var getYoutubeEmbedUrl = (youtubeUrl) => {
  if (!youtubeUrl) return null;
  try {
    const url = new URL(youtubeUrl);
    let embedUrl = null;
    if (url.hostname.includes("youtu.be")) {
      const videoId = url.pathname.replace("/", "");
      embedUrl = videoId ? new URL(`https://www.youtube.com/embed/${videoId}`) : null;
    }
    if (!embedUrl && url.hostname.includes("youtube.com")) {
      if (url.pathname.startsWith("/embed/")) {
        const videoId = url.pathname.replace("/embed/", "");
        embedUrl = videoId ? new URL(`https://www.youtube.com/embed/${videoId}`) : null;
      }
      if (!embedUrl) {
        const videoId = url.searchParams.get("v");
        embedUrl = videoId ? new URL(`https://www.youtube.com/embed/${videoId}`) : null;
      }
    }
    if (!embedUrl) return null;
    embedUrl.searchParams.set("fs", "1");
    embedUrl.searchParams.set("rel", "0");
    return embedUrl.toString();
  } catch (_error) {
    return null;
  }
};
var filterFeatureGuideVideos = (collection) => (collection?.items ?? []).map(
  (item) => normalizeFeatureGuideVideoData(item.fields, item.sys.id)
);

// src/feature-guide/components/FeatureGuideModal.tsx
import {
  Banner as Banner2,
  BlockStack as BlockStack3,
  Box as Box2,
  Checkbox,
  Link as Link2,
  Modal,
  Spinner,
  Text as Text4
} from "@shopify/polaris";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var YOUTUBE_IFRAME_ALLOW_PERMISSIONS = {
  // Lets the player react to device motion data where supported.
  ACCELEROMETER: "accelerometer",
  // Allows playback to start automatically when the embed requests it.
  AUTOPLAY: "autoplay",
  // Supports copy actions initiated from the embedded player UI.
  CLIPBOARD_WRITE: "clipboard-write",
  // Enables protected media playback required by modern video embeds.
  ENCRYPTED_MEDIA: "encrypted-media",
  // Allows the YouTube player to enter fullscreen mode.
  FULLSCREEN: "fullscreen",
  // Lets the player react to device orientation data where supported.
  GYROSCOPE: "gyroscope",
  // Allows the browser's picture-in-picture video mode.
  PICTURE_IN_PICTURE: "picture-in-picture"
};
var FeatureGuideModal = ({
  open,
  onClose,
  guide,
  loading = false,
  error = null,
  dontShowAgainChecked = false,
  onDontShowAgainChange,
  primaryActionLabel = "Done",
  closeActionLabel = "Close",
  fallbackActionLabel = "Open fallback"
}) => {
  const embedUrl = getYoutubeEmbedUrl(guide?.youtubeUrl);
  return /* @__PURE__ */ jsx5(
    Modal,
    {
      open,
      onClose,
      title: guide?.title ?? "Guide video",
      primaryAction: {
        content: primaryActionLabel,
        onAction: onClose
      },
      secondaryActions: [
        {
          content: closeActionLabel,
          onAction: onClose
        }
      ],
      children: /* @__PURE__ */ jsx5(Modal.Section, { children: /* @__PURE__ */ jsxs5(BlockStack3, { gap: "400", children: [
        loading ? /* @__PURE__ */ jsx5(Box2, { padding: "600", children: /* @__PURE__ */ jsx5("div", { style: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx5(Spinner, { accessibilityLabel: "Loading guide video", size: "large" }) }) }) : null,
        !loading && error ? /* @__PURE__ */ jsx5(Banner2, { tone: "critical", title: "Video could not be loaded", children: /* @__PURE__ */ jsx5("p", { children: error }) }) : null,
        !loading && guide?.description ? /* @__PURE__ */ jsx5(Text4, { as: "p", variant: "bodyMd", tone: "subdued", children: guide.description }) : null,
        !loading && embedUrl ? /* @__PURE__ */ jsx5(
          "div",
          {
            style: {
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              overflow: "hidden",
              borderRadius: "12px",
              backgroundColor: "#000"
            },
            children: /* @__PURE__ */ jsx5(
              "iframe",
              {
                src: embedUrl,
                title: guide?.title ?? "Guide video",
                allow: Object.values(YOUTUBE_IFRAME_ALLOW_PERMISSIONS).join("; "),
                allowFullScreen: true,
                referrerPolicy: "strict-origin-when-cross-origin",
                style: {
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0
                }
              }
            )
          }
        ) : null,
        !loading && !embedUrl && guide?.fallbackUrl ? /* @__PURE__ */ jsx5(Banner2, { tone: "warning", title: "Embedded video is unavailable", children: /* @__PURE__ */ jsxs5("p", { children: [
          "Open the fallback link to view this guide.",
          " ",
          /* @__PURE__ */ jsx5(Link2, { url: guide.fallbackUrl, target: "_blank", removeUnderline: false, children: fallbackActionLabel })
        ] }) }) : null,
        !loading && !embedUrl && !guide?.fallbackUrl && !error ? /* @__PURE__ */ jsx5(Banner2, { tone: "warning", title: "Guide video is unavailable", children: /* @__PURE__ */ jsx5("p", { children: "No playable video or fallback link is configured." }) }) : null,
        onDontShowAgainChange ? /* @__PURE__ */ jsx5(
          Checkbox,
          {
            label: "Don't show again",
            checked: dontShowAgainChecked,
            onChange: onDontShowAgainChange
          }
        ) : null
      ] }) })
    }
  );
};

// src/feature-guide/components/FeatureGuideTriggerButton.tsx
import { CirclePlay } from "lucide-react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var FeatureGuideTriggerButton = ({
  disabled = false,
  label = "Watch video",
  onClick
}) => /* @__PURE__ */ jsx6(
  "div",
  {
    style: {
      position: "fixed",
      left: "68px",
      bottom: "24px",
      zIndex: 40
    },
    children: /* @__PURE__ */ jsxs6(
      "button",
      {
        type: "button",
        onClick,
        disabled,
        "aria-label": label,
        style: {
          height: "36px",
          border: "1px solid rgba(138, 138, 138, 0.3)",
          borderRadius: "999px",
          background: "var(--p-color-bg-surface, #ffffff)",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "0 16px",
          color: "#303030",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: 1
        },
        children: [
          /* @__PURE__ */ jsx6(CirclePlay, { size: 18, strokeWidth: 1.8 }),
          /* @__PURE__ */ jsx6("span", { children: label })
        ]
      }
    )
  }
);

// src/feature-guide/components/FeatureGuide.tsx
import { Fragment as Fragment2, jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var FeatureGuide = ({
  app,
  appSectionId,
  getFeatureGuideVideos,
  open,
  onOpen,
  onClose,
  dontShowAgainChecked = false,
  onDontShowAgainChange,
  triggerLabel,
  hideTriggerWhenUnavailable = true,
  onGuideLoaded
}) => {
  const [guide, setGuide] = useState4(null);
  const [loading, setLoading] = useState4(true);
  const [error, setError] = useState4(null);
  useEffect3(() => {
    let active = true;
    const loadGuide = async () => {
      try {
        setLoading(true);
        setError(null);
        const nextGuide = await getActiveFeatureGuide({
          app,
          appSectionId,
          getFeatureGuideVideos
        });
        if (!active) return;
        setGuide(nextGuide);
        onGuideLoaded?.(nextGuide);
      } catch (_error) {
        if (!active) return;
        setError("Please try again later or use the fallback link.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    loadGuide();
    return () => {
      active = false;
    };
  }, [app, appSectionId, getFeatureGuideVideos, onGuideLoaded]);
  const shouldHideTrigger = hideTriggerWhenUnavailable && !loading && !guide;
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    !shouldHideTrigger ? /* @__PURE__ */ jsx7(
      FeatureGuideTriggerButton,
      {
        disabled: loading,
        label: triggerLabel,
        onClick: onOpen
      }
    ) : null,
    /* @__PURE__ */ jsx7(
      FeatureGuideModal,
      {
        open,
        onClose,
        guide,
        loading,
        error,
        dontShowAgainChecked,
        onDontShowAgainChange
      }
    )
  ] });
};

// src/whats-new/components/WhatsNewContent.tsx
import {
  Badge,
  BlockStack as BlockStack4,
  Card as Card4,
  InlineStack as InlineStack2,
  Text as Text5
} from "@shopify/polaris";

// src/whats-new/components/WhatsNewContent.css
styleInject(".whats-new-content {\n  max-width: 100%;\n}\n.whats-new-content__list {\n  margin: 0;\n  padding-left: 0;\n  list-style-position: inside;\n}\n.whats-new-content__list-item + .whats-new-content__list-item {\n  margin-top: 0.25rem;\n}\n");

// src/whats-new/components/WhatsNewContent.tsx
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var WhatsNewContent = ({
  sections,
  loading = false,
  loadingMessage = "Loading updates...",
  emptyState
}) => {
  if (loading) {
    return /* @__PURE__ */ jsx8(Card4, { roundedAbove: "xs", children: /* @__PURE__ */ jsx8(BlockStack4, { gap: "200", children: /* @__PURE__ */ jsx8(Text5, { as: "h3", variant: "headingMd", children: loadingMessage }) }) });
  }
  if (!sections.length) {
    if (!emptyState) return null;
    return /* @__PURE__ */ jsx8(Card4, { roundedAbove: "xs", children: /* @__PURE__ */ jsxs8(BlockStack4, { gap: "200", children: [
      /* @__PURE__ */ jsx8(Text5, { as: "h3", variant: "headingMd", children: emptyState.title }),
      emptyState.description ? /* @__PURE__ */ jsx8(Text5, { as: "p", variant: "bodyMd", tone: "subdued", children: emptyState.description }) : null
    ] }) });
  }
  return /* @__PURE__ */ jsx8("div", { className: "whats-new-content", children: /* @__PURE__ */ jsx8(BlockStack4, { gap: "400", children: sections.flatMap(
    (section) => section.entries.map((entry, index) => /* @__PURE__ */ jsx8(
      Card4,
      {
        roundedAbove: "xs",
        children: /* @__PURE__ */ jsxs8(BlockStack4, { gap: "300", children: [
          /* @__PURE__ */ jsxs8(InlineStack2, { align: "space-between", blockAlign: "start", gap: "200", children: [
            /* @__PURE__ */ jsx8(Text5, { as: "h3", variant: "headingMd", children: entry.title ?? section.title }),
            section.badge ? /* @__PURE__ */ jsx8(Badge, { tone: section.badgeTone, children: section.badge }) : null
          ] }),
          entry.label ? /* @__PURE__ */ jsx8(Text5, { as: "p", variant: "bodyMd", tone: "subdued", children: entry.label }) : null,
          entry.description ? /* @__PURE__ */ jsx8("div", { style: { whiteSpace: "pre-line" }, children: /* @__PURE__ */ jsx8(Text5, { as: "p", variant: "bodyMd", children: entry.description }) }) : null,
          entry.items?.length ? /* @__PURE__ */ jsx8("ul", { className: "whats-new-content__list", children: entry.items.map((item) => /* @__PURE__ */ jsx8(
            "li",
            {
              className: "whats-new-content__list-item",
              children: /* @__PURE__ */ jsx8(Text5, { as: "span", variant: "bodyMd", children: item })
            },
            `${entry.label ?? section.title}-${item}`
          )) }) : null
        ] })
      },
      entry.id ?? `${section.title}-${entry.label ?? index}`
    ))
  ) }) });
};

// src/whats-new/utils/changelog.ts
var WHATS_NEW_CHANGELOG_URLS = {
  [FordeerApp.Invoice]: "https://invoice.changelog.fordeer.app/",
  [FordeerApp.Label]: "https://label.changelog.fordeer.app/",
  [FordeerApp.ReelPik]: "https://reelpik.changelog.fordeer.app/"
};
var getWhatsNewChangelogUrl = (app) => {
  if (!app) return void 0;
  return WHATS_NEW_CHANGELOG_URLS[normalizeAppIdentifier(app)];
};

// src/whats-new/components/WhatsNewModal.tsx
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var WhatsNewModal = ({
  open,
  onOpen,
  onClose,
  app,
  title = "What's new",
  closeActionLabel = "Close",
  sections,
  loading,
  loadingMessage,
  emptyState,
  renderModal,
  children,
  renderTrigger
}) => {
  const changelogUrl = getWhatsNewChangelogUrl(app);
  const triggerProps = {
    open,
    toggle: () => {
      if (open) {
        onClose();
        return;
      }
      onOpen();
    },
    openModal: onOpen,
    closeModal: onClose
  };
  const triggerContent = typeof renderTrigger === "function" ? renderTrigger(triggerProps) : typeof children === "function" ? children(triggerProps) : children ?? null;
  return /* @__PURE__ */ jsxs9(Fragment3, { children: [
    triggerContent,
    renderModal({
      open,
      title,
      closeActionLabel,
      onClose,
      changelogAction: changelogUrl ? {
        content: "View Full Changelog",
        onAction: () => {
          window.open(changelogUrl, "_blank", "noopener,noreferrer");
        }
      } : void 0,
      content: /* @__PURE__ */ jsx9(
        WhatsNewContent,
        {
          sections,
          loading,
          loadingMessage,
          emptyState
        }
      )
    })
  ] });
};

// src/whats-new/components/WhatsNewTeaser.tsx
import { Button as Button3, Text as Text6 } from "@shopify/polaris";
import { X as X3 } from "lucide-react";

// src/whats-new/components/WhatsNewTeaser.css
styleInject(".whats-new-teaser {\n  position: fixed;\n  left: 24px;\n  bottom: 76px;\n  z-index: 45;\n  width: min(300px, calc(100vw - 100px));\n  max-height: calc(100vh - 100px);\n  overflow: hidden auto;\n  border: 1px solid rgb(227 227 227 / 92%);\n  border-radius: 12px;\n  background: var(--p-color-bg-surface);\n  box-shadow: 0 18px 44px rgb(15 23 42 / 18%), 0 3px 12px rgb(15 23 42 / 10%);\n}\n.whats-new-teaser__close {\n  position: absolute;\n  top: 10px;\n  right: 10px;\n  z-index: 1;\n  display: grid;\n  width: 26px;\n  height: 26px;\n  place-items: center;\n  border: 0;\n  border-radius: 50%;\n  background: rgb(255 255 255 / 88%);\n  color: var(--p-color-text-subdued);\n  cursor: pointer;\n  box-shadow: 0 1px 4px rgb(0 0 0 / 10%);\n}\n.whats-new-teaser__close svg {\n  width: 16px;\n  height: 16px;\n}\n.whats-new-teaser__close:hover {\n  background: var(--p-color-bg-surface);\n  color: var(--p-color-text);\n}\n.whats-new-teaser__media {\n  display: block;\n  width: 100%;\n  height: 164px;\n  object-fit: cover;\n}\n.whats-new-teaser__content {\n  display: grid;\n  gap: 10px;\n  padding: 18px 20px 20px;\n  border-top: 1px solid rgb(227 227 227 / 92%);\n}\n.whats-new-teaser__badge {\n  justify-self: start;\n  width: max-content;\n  max-width: 100%;\n  border-radius: 999px;\n  padding: 2px 9px;\n  font-size: 11px;\n  font-weight: 700;\n  line-height: 16px;\n  text-transform: uppercase;\n}\n.whats-new-teaser__badge--info {\n  background: #d9efff;\n  color: #00527c;\n}\n.whats-new-teaser__badge--success {\n  background: #dbf4e8;\n  color: #0b6b3a;\n}\n.whats-new-teaser__actions {\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  gap: 12px;\n  padding-top: 2px;\n}\n@media (max-width: 480px) {\n  .whats-new-teaser {\n    left: 16px;\n    right: 16px;\n    bottom: 68px;\n    width: auto;\n  }\n  .whats-new-teaser__media {\n    height: 150px;\n  }\n}\n");

// src/whats-new/components/WhatsNewTeaser.tsx
import { jsx as jsx10, jsxs as jsxs10 } from "react/jsx-runtime";
var TEASER_FALLBACK_IMAGE = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="680" height="360" viewBox="0 0 680 360"><rect fill="#fff3ec" width="680" height="360"/><rect x="120" y="42" width="440" height="250" rx="16" fill="#ffffff"/><rect x="120" y="42" width="112" height="250" fill="#fbfbfb"/><path d="M232 42v250" stroke="#d8d8d8" stroke-width="3"/><text x="260" y="84" fill="#111827" font-family="Arial" font-size="30" font-weight="700">Updates</text><rect x="420" y="66" width="100" height="22" rx="6" fill="#f4f4f5"/><rect x="260" y="116" width="92" height="92" rx="10" fill="#ffeadc"/><rect x="374" y="116" width="92" height="92" rx="10" fill="#d6f7ff"/><rect x="488" y="116" width="92" height="92" rx="10" fill="#ffe2ef"/><rect x="260" y="228" width="92" height="44" rx="10" fill="#fff9d8"/><rect x="374" y="228" width="92" height="44" rx="10" fill="#d8f8ea"/><rect x="488" y="228" width="92" height="44" rx="10" fill="#f3f4f6"/><rect x="148" y="126" width="54" height="12" rx="6" fill="#ededed"/><rect x="148" y="158" width="72" height="12" rx="6" fill="#ededed"/><rect x="148" y="190" width="44" height="12" rx="6" fill="#ededed"/></svg>'
);
var WhatsNewTeaser = ({
  entry,
  visible,
  titleId = "whats-new-teaser-title",
  learnMoreLabel = "Learn more",
  dismissLabel = "Got it",
  closeLabel = "Close update teaser",
  fallbackDescription = "Read the full update details.",
  onOpenDetail,
  onDismiss
}) => {
  if (!visible) return null;
  const image = entry.teaserImageUrl ?? TEASER_FALLBACK_IMAGE;
  return /* @__PURE__ */ jsxs10("section", { className: "whats-new-teaser", "aria-labelledby": titleId, children: [
    /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        className: "whats-new-teaser__close",
        "aria-label": closeLabel,
        onClick: onDismiss,
        children: /* @__PURE__ */ jsx10(X3, { size: 15, strokeWidth: 2 })
      }
    ),
    /* @__PURE__ */ jsx10("img", { className: "whats-new-teaser__media", src: image, alt: "" }),
    /* @__PURE__ */ jsxs10("div", { className: "whats-new-teaser__content", children: [
      /* @__PURE__ */ jsx10(
        "span",
        {
          className: `whats-new-teaser__badge whats-new-teaser__badge--${entry.labelTone}`,
          children: entry.label
        }
      ),
      /* @__PURE__ */ jsx10(Text6, { as: "h2", variant: "headingMd", fontWeight: "bold", children: /* @__PURE__ */ jsx10("span", { id: titleId, children: entry.teaserTitle }) }),
      /* @__PURE__ */ jsx10(Text6, { as: "p", variant: "bodySm", children: entry.teaserShortDescription || fallbackDescription }),
      /* @__PURE__ */ jsxs10("div", { className: "whats-new-teaser__actions", children: [
        /* @__PURE__ */ jsx10(Button3, { variant: "plain", onClick: onOpenDetail, children: learnMoreLabel }),
        /* @__PURE__ */ jsx10(Button3, { variant: "primary", onClick: onDismiss, children: dismissLabel })
      ] })
    ] })
  ] });
};

// src/whats-new/types/index.ts
var WhatsNewType = {
  Announcement: "announcement",
  ProductUpdate: "productUpdate",
  News: "news"
};

// src/whats-new/constants/index.ts
var WHATS_NEW_SECTION_CONFIG = {
  announcement: {
    title: "Announcements",
    badge: "Announcement",
    badgeTone: "info"
  },
  productUpdate: {
    title: "Product updates",
    badge: "Product update",
    badgeTone: "success"
  },
  news: {
    title: "News",
    badge: "News",
    badgeTone: "attention"
  }
};

// src/whats-new/utils/contentful.ts
import { format, parseISO as parseISO4 } from "date-fns";
var getAppSlug3 = (app) => normalizeAppIdentifier(app);
var getLinkedAppSlug3 = (entry) => normalizeAppIdentifier(entry?.fields.slug);
var matchesApp3 = (app, linkedApp) => {
  if (!linkedApp) return false;
  return getAppSlug3(app) === getLinkedAppSlug3(linkedApp);
};
var extractNodeText = (node) => {
  if (!node) return "";
  if (typeof node.value === "string") {
    return node.value;
  }
  if (!Array.isArray(node.content)) {
    return "";
  }
  return node.content.map((child) => extractNodeText(child)).join("");
};
var normalizeRichTextContent = (document2) => {
  if (!document2?.content?.length) {
    return {};
  }
  const descriptionBlocks = [];
  const items = [];
  document2.content.forEach((node) => {
    if (node.nodeType === "unordered-list" || node.nodeType === "ordered-list") {
      node.content?.forEach((listItem) => {
        const text2 = extractNodeText(listItem).trim();
        if (text2) {
          items.push(text2);
        }
      });
      return;
    }
    const text = extractNodeText(node).trim();
    if (text) {
      descriptionBlocks.push(text);
    }
  });
  return {
    description: descriptionBlocks.join("\n\n") || void 0,
    items: items.length ? items : void 0
  };
};
var getEntryTimestamp = (date) => {
  if (!date) return 0;
  const parsedDate = parseISO4(date);
  return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
};
var getContentfulEntryTimestamp = (entry) => {
  const dateTimestamp = getEntryTimestamp(entry.fields.date);
  if (dateTimestamp) return dateTimestamp;
  return getEntryTimestamp(entry.sys.createdAt);
};
var getTeaserLabel = (type) => type === "productUpdate" ? { label: "Improvement", labelTone: "success" } : { label: "New", labelTone: "info" };
var formatEntryLabel = (date) => {
  if (!date) return void 0;
  const parsedDate = parseISO4(date);
  if (Number.isNaN(parsedDate.getTime())) return void 0;
  return format(parsedDate, "MMMM d, yyyy");
};
async function getActiveWhatsNewSections({
  app,
  getWhatsNewEntries
}) {
  const collection = await getWhatsNewEntries();
  if (!collection?.items.length) return [];
  const sections = collection.items.reduce(
    (acc, entry) => {
      if (!matchesApp3(app, entry.fields.fordeerApp)) {
        return acc;
      }
      const { fields } = entry;
      const sectionConfig = WHATS_NEW_SECTION_CONFIG[fields.type];
      const richTextContent = normalizeRichTextContent(fields.description);
      const nextTimestamp = getEntryTimestamp(fields.date);
      const nextSection = {
        timestamp: nextTimestamp,
        section: {
          id: entry.sys.id,
          title: sectionConfig.title,
          badge: sectionConfig.badge,
          badgeTone: sectionConfig.badgeTone,
          entries: [
            {
              id: entry.sys.id,
              label: formatEntryLabel(fields.date),
              title: fields.title ?? void 0,
              description: richTextContent.description,
              items: richTextContent.items
            }
          ]
        }
      };
      const insertIndex = acc.findIndex(
        (sectionWithTimestamp) => sectionWithTimestamp.timestamp < nextTimestamp
      );
      if (insertIndex === -1) {
        acc.push(nextSection);
        return acc;
      }
      acc.splice(insertIndex, 0, nextSection);
      return acc;
    },
    []
  );
  return sections.map((sectionWithTimestamp) => sectionWithTimestamp.section);
}
async function getActiveWhatsNewTeasers({
  app,
  getWhatsNewEntries
}) {
  const collection = await getWhatsNewEntries();
  if (!collection?.items.length) return [];
  return collection.items.filter((entry) => matchesApp3(app, entry.fields.fordeerApp)).sort((a, b) => getContentfulEntryTimestamp(b) - getContentfulEntryTimestamp(a)).map((entry) => {
    const { fields } = entry;
    const teaserTitle = (fields.teaserTitle?.trim() || fields.title?.trim() || "Update").trim();
    const detailHeading = (fields.title?.trim() || teaserTitle).trim();
    return {
      id: entry.sys.id,
      createdAt: entry.sys.createdAt,
      date: fields.date,
      rawType: fields.type,
      ...getTeaserLabel(fields.type),
      teaserTitle,
      teaserShortDescription: fields.teaserShortDescription?.trim() ?? "",
      teaserImageUrl: toContentfulAssetUrl(fields.teaserThumbnail),
      detailHeading,
      description: fields.description
    };
  });
}

// src/pricing-social-proof/components/MerchantLogosMarquee.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var MerchantLogosMarquee = ({
  merchants
}) => {
  if (!merchants.length) return null;
  return /* @__PURE__ */ jsx11(Marquee, { speed: 22, gap: 16, ariaLabel: "Trusted merchants", children: merchants.map((merchant) => /* @__PURE__ */ jsx11("div", { className: "merchant-logos__card", children: /* @__PURE__ */ jsx11("img", { src: merchant.logoUrl, alt: merchant.companyName, loading: "lazy" }) }, merchant.id)) });
};

// src/pricing-social-proof/components/TestimonialsMarquee.tsx
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
var renderRating = (rating) => {
  if (!rating) return null;
  const safeRating = Math.max(1, Math.min(5, Math.round(rating)));
  return "\u2605\u2605\u2605\u2605\u2605".slice(0, safeRating);
};
var renderCountryLabel = (country) => {
  if (!country) return void 0;
  const flag = countryNameToFlag(country);
  return flag ? `${flag}\xA0${country}` : country;
};
var TestimonialsMarquee = ({
  testimonials
}) => {
  if (!testimonials.length) return null;
  return /* @__PURE__ */ jsx12(Marquee, { speed: 42, gap: 16, ariaLabel: "Merchant testimonials", children: testimonials.map((testimonial) => {
    const rating = renderRating(testimonial.rating);
    const countryLabel = renderCountryLabel(testimonial.country);
    return /* @__PURE__ */ jsxs11("article", { className: "testimonials-marquee__card", children: [
      /* @__PURE__ */ jsxs11("div", { className: "testimonials-marquee__header", children: [
        /* @__PURE__ */ jsx12("p", { className: "testimonials-marquee__attribution", children: testimonial.merchantName }),
        rating ? /* @__PURE__ */ jsx12(
          "div",
          {
            className: "testimonials-marquee__rating",
            "aria-label": `${testimonial.rating} out of 5 stars`,
            children: rating
          }
        ) : null
      ] }),
      /* @__PURE__ */ jsxs11("p", { className: "testimonials-marquee__quote", children: [
        "\u201C",
        testimonial.testimonial,
        "\u201D"
      ] }),
      countryLabel || testimonial.appUsageDuration ? /* @__PURE__ */ jsx12("p", { className: "testimonials-marquee__meta", children: [countryLabel, testimonial.appUsageDuration].filter(Boolean).join("\xA0\xB7\xA0") }) : null
    ] }, testimonial.id);
  }) });
};

// src/pricing-social-proof/components/PricingSocialProof.tsx
import { useEffect as useEffect4, useState as useState5 } from "react";

// src/pricing-social-proof/utils/contentful.ts
var DEFAULT_MERCHANT_LIMIT = 5;
var DEFAULT_TESTIMONIAL_LIMIT = 10;
var matchesApp4 = (app, linkedApp) => {
  const normalizedApp = normalizeAppIdentifier(app);
  const linkedValues = getStringFieldValues(linkedApp?.fields);
  return linkedValues.some((value) => normalizeAppIdentifier(value) === normalizedApp);
};
var getEntryTimestamp2 = (entry) => {
  const date = entry.fields?.createdAt ?? entry.sys.createdAt;
  if (!date) return 0;
  const timestamp = new Date(date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};
var normalizeMerchant = (fields) => {
  const merchant = fields.merchant;
  const companyName = merchant?.fields?.companyName?.trim();
  const logoUrl = toContentfulAssetUrl(merchant?.fields?.logo);
  if (!merchant?.sys.id || !companyName || !logoUrl) return null;
  return {
    id: merchant.sys.id,
    companyName,
    logoUrl,
    companyDescription: merchant.fields?.companyDescription?.trim() || void 0,
    companyWebsite: merchant.fields?.companyWebsite?.trim() || void 0,
    country: merchant.fields?.country?.trim() || void 0,
    appUsageDuration: merchant.fields?.appUsageDuration?.trim() || void 0
  };
};
var normalizeTestimonial = (entry) => {
  const fields = entry.fields;
  const testimonial = fields?.testimonial?.trim();
  const merchantName = fields?.merchant?.fields?.companyName?.trim();
  if (!fields || !testimonial || !merchantName) return null;
  return {
    id: entry.sys.id,
    merchantName,
    testimonial,
    rating: fields.rating,
    createdAt: fields.createdAt ?? entry.sys.createdAt,
    country: fields.merchant?.fields?.country?.trim() || void 0,
    appUsageDuration: fields.merchant?.fields?.appUsageDuration?.trim() || void 0
  };
};
async function getActivePricingSocialProof({
  app,
  getTestimonials,
  merchantLimit = DEFAULT_MERCHANT_LIMIT,
  testimonialLimit = DEFAULT_TESTIMONIAL_LIMIT
}) {
  try {
    const collection = await getTestimonials();
    if (!collection?.items.length) return { merchants: [], testimonials: [] };
    const matchingEntries = collection.items.filter((entry) => matchesApp4(app, entry.fields?.app)).sort((left, right) => getEntryTimestamp2(right) - getEntryTimestamp2(left));
    const merchantsById = /* @__PURE__ */ new Map();
    matchingEntries.forEach((entry) => {
      if (!entry.fields) return;
      const merchant = normalizeMerchant(entry.fields);
      if (merchant && !merchantsById.has(merchant.id)) {
        merchantsById.set(merchant.id, merchant);
      }
    });
    return {
      merchants: Array.from(merchantsById.values()).slice(0, merchantLimit),
      testimonials: matchingEntries.map(normalizeTestimonial).filter((testimonial) => Boolean(testimonial)).slice(0, testimonialLimit)
    };
  } catch (error) {
    console.error("Error fetching pricing social proof entries:", error);
    return { merchants: [], testimonials: [] };
  }
}

// src/pricing-social-proof/components/PricingSocialProof.css
styleInject(".pricing-social-proof {\n  border: 1px solid #e3e3e3;\n  border-radius: 12px;\n  background: #ffffff;\n  padding: 36px 0;\n  overflow: hidden;\n}\n.pricing-social-proof__header {\n  padding: 0 24px 28px;\n  text-align: center;\n}\n.pricing-social-proof__header h2 {\n  margin: 0;\n  color: #202223;\n  font-size: clamp(21px, 2.5vw, 24px);\n  font-weight: 600;\n  line-height: 1.3;\n}\n.pricing-social-proof__marquees {\n  display: flex;\n  flex-direction: column;\n  gap: 20px;\n}\n.merchant-logos__card {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex: 0 0 152px;\n  height: 68px;\n  padding: 0 12px;\n  border: 1px solid #e3e3e3;\n  border-radius: 10px;\n  background: #ffffff;\n  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);\n}\n.merchant-logos__card img {\n  max-width: 110px;\n  max-height: 40px;\n  object-fit: contain;\n}\n.testimonials-marquee__card {\n  flex: 0 0 304px;\n  min-height: 140px;\n  padding: 20px;\n  border: 1px solid #e3e3e3;\n  border-radius: 10px;\n  background: #ffffff;\n  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);\n}\n.testimonials-marquee__header {\n  margin-bottom: 12px;\n}\n.testimonials-marquee__rating {\n  color: #f2b100;\n  font-size: 18px;\n  letter-spacing: 0.06em;\n  line-height: 1;\n  margin-top: 4px;\n}\n.testimonials-marquee__quote {\n  margin: 0 0 8px;\n  color: #202223;\n  font-size: 14px;\n  line-height: 1.45;\n}\n.testimonials-marquee__attribution {\n  margin: 0;\n  color: #202223;\n  font-size: 16px;\n  font-weight: 600;\n}\n.testimonials-marquee__meta {\n  margin: 0;\n  color: #8e9091;\n  font-size: 14px;\n  line-height: 1.4;\n}\n@media (max-width: 640px) {\n  .pricing-social-proof {\n    border-radius: 8px;\n    padding: 28px 0;\n  }\n  .pricing-social-proof__header {\n    padding: 0 16px 22px;\n  }\n  .pricing-social-proof__marquees {\n    gap: 28px;\n  }\n  .merchant-logos__card {\n    flex-basis: 128px;\n    height: 60px;\n  }\n  .merchant-logos__card img {\n    max-width: 92px;\n    max-height: 34px;\n  }\n  .testimonials-marquee__card {\n    flex-basis: min(264px, calc(100vw - 56px));\n    min-height: 136px;\n  }\n}\n");

// src/pricing-social-proof/components/PricingSocialProof.tsx
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var EMPTY_CONTENT = {
  merchants: [],
  testimonials: []
};
var PricingSocialProof = ({
  app,
  getTestimonials,
  merchantLimit,
  testimonialLimit,
  title = "Trusted by growing Shopify stores"
}) => {
  const [content, setContent] = useState5(EMPTY_CONTENT);
  useEffect4(() => {
    let isMounted = true;
    const fetchSocialProof = async () => {
      if (!getTestimonials) {
        setContent(EMPTY_CONTENT);
        return;
      }
      const nextContent = await getActivePricingSocialProof({
        app,
        getTestimonials,
        merchantLimit,
        testimonialLimit
      });
      if (isMounted) {
        setContent(nextContent);
      }
    };
    fetchSocialProof();
    return () => {
      isMounted = false;
    };
  }, [app, getTestimonials, merchantLimit, testimonialLimit]);
  if (!content.merchants.length && !content.testimonials.length) return null;
  return /* @__PURE__ */ jsxs12("section", { className: "pricing-social-proof", "aria-label": "Pricing social proof", children: [
    /* @__PURE__ */ jsx13("div", { className: "pricing-social-proof__header", children: /* @__PURE__ */ jsx13("h2", { children: title }) }),
    /* @__PURE__ */ jsxs12("div", { className: "pricing-social-proof__marquees", children: [
      /* @__PURE__ */ jsx13(MerchantLogosMarquee, { merchants: content.merchants }),
      /* @__PURE__ */ jsx13(TestimonialsMarquee, { testimonials: content.testimonials })
    ] })
  ] });
};

// src/pricing-trust-metrics/components/PricingTrustMetrics.tsx
import { BlockStack as BlockStack5, InlineStack as InlineStack3, Text as Text7 } from "@shopify/polaris";
import { useEffect as useEffect5, useState as useState6 } from "react";

// src/pricing-trust-metrics/utils/contentful.ts
var matchesApp5 = (app, linkedApp) => {
  const normalizedApp = normalizeAppIdentifier(app);
  const linkedValues = getStringFieldValues(linkedApp?.fields);
  return linkedValues.some((value) => normalizeAppIdentifier(value) === normalizedApp);
};
var getSortOrder = (sortOrder) => {
  if (typeof sortOrder !== "number" || Number.isNaN(sortOrder)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return sortOrder;
};
var normalizeTrustMetric = (entry) => {
  const fields = entry.fields;
  const value = fields?.value?.trim();
  const label = fields?.label?.trim();
  if (!fields || fields.isActive === false || !value || !label) return null;
  return {
    id: entry.sys.id,
    value,
    label,
    iconUrl: toContentfulAssetUrl(fields.icon),
    sortOrder: fields.sortOrder
  };
};
async function getActivePricingTrustMetrics({
  app,
  getTrustMetrics
}) {
  try {
    const collection = await getTrustMetrics();
    if (!collection?.items.length) return [];
    return collection.items.reduce((metrics, entry) => {
      if (!matchesApp5(app, entry.fields?.app)) return metrics;
      const metric = normalizeTrustMetric(entry);
      if (metric) metrics.push(metric);
      return metrics;
    }, []).sort((left, right) => getSortOrder(left.sortOrder) - getSortOrder(right.sortOrder));
  } catch (error) {
    console.error("Error fetching pricing trust metrics:", error);
    return [];
  }
}

// src/pricing-trust-metrics/components/PricingTrustMetrics.css
styleInject(".pricing-trust-metrics {\n  width: 100%;\n}\n.pricing-trust-metrics__row {\n  display: grid;\n  grid-template-columns: repeat(4, minmax(0, 1fr));\n  gap: 12px;\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n.pricing-trust-metrics__card {\n  min-width: 0;\n  padding: 16px;\n  border: 1px solid #e3e3e3;\n  border-radius: 10px;\n  background: #ffffff;\n  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);\n}\n.pricing-trust-metrics__icon {\n  flex: 0 0 24px;\n  width: 24px;\n  height: 24px;\n  object-fit: contain;\n}\n@media (max-width: 640px) {\n  .pricing-trust-metrics__row {\n    grid-template-columns: 1fr;\n    gap: 10px;\n  }\n  .pricing-trust-metrics__card {\n    padding: 14px;\n  }\n}\n");

// src/pricing-trust-metrics/components/PricingTrustMetrics.tsx
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var PricingTrustMetrics = ({
  app,
  getTrustMetrics
}) => {
  const [metrics, setMetrics] = useState6([]);
  useEffect5(() => {
    let isMounted = true;
    const fetchMetrics = async () => {
      if (!getTrustMetrics) {
        setMetrics([]);
        return;
      }
      const nextMetrics = await getActivePricingTrustMetrics({
        app,
        getTrustMetrics
      });
      if (isMounted) {
        setMetrics(nextMetrics);
      }
    };
    fetchMetrics();
    return () => {
      isMounted = false;
    };
  }, [app, getTrustMetrics]);
  if (!metrics.length) return null;
  return /* @__PURE__ */ jsx14("section", { className: "pricing-trust-metrics", "aria-label": "Trust metrics", children: /* @__PURE__ */ jsx14("ul", { className: "pricing-trust-metrics__row", children: metrics.map((metric) => /* @__PURE__ */ jsx14("li", { className: "pricing-trust-metrics__card", children: /* @__PURE__ */ jsxs13(BlockStack5, { gap: "300", children: [
    /* @__PURE__ */ jsx14(Text7, { as: "h3", variant: "headingSm", children: metric.label }),
    /* @__PURE__ */ jsxs13(InlineStack3, { gap: "200", blockAlign: "center", wrap: false, children: [
      metric.iconUrl ? /* @__PURE__ */ jsx14(
        "img",
        {
          className: "pricing-trust-metrics__icon",
          src: metric.iconUrl,
          alt: "",
          loading: "lazy"
        }
      ) : null,
      /* @__PURE__ */ jsx14(Text7, { as: "p", variant: "headingLg", fontWeight: "semibold", children: metric.value })
    ] })
  ] }) }, metric.id)) }) });
};

// src/recommended-apps/components/RecommendedAppCard.tsx
import { Badge as Badge2, BlockStack as BlockStack6, Button as Button4, Text as Text8 } from "@shopify/polaris";
import { Download, ExternalLink } from "lucide-react";

// src/recommended-apps/components/RecommendedApps.css
styleInject(".recommendedApps__logo {\n  width: 64px;\n  height: 64px;\n  flex: 0 0 64px;\n  border-radius: var(--p-border-radius-300);\n  border: var(--p-border-width-025) solid var(--p-color-border-secondary);\n  background: var(--p-color-bg-surface);\n  object-fit: contain;\n}\n.recommendedApps__logoPlaceholder {\n  width: 64px;\n  height: 64px;\n  flex: 0 0 64px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: var(--p-border-radius-300);\n  background: var(--p-color-bg-surface-secondary);\n  color: var(--p-color-text-secondary);\n  font-weight: var(--p-font-weight-semibold);\n}\n.recommendedApps__partnerItem {\n  position: relative;\n  width: 100%;\n  min-width: 0;\n  display: flex;\n  gap: var(--p-space-400);\n  align-items: flex-start;\n  padding: var(--p-space-500);\n  padding-right: calc(var(--p-space-500) + 28px);\n  border: var(--p-border-width-025) solid var(--p-color-border-secondary);\n  border-radius: var(--p-border-radius-400);\n  background: var(--p-color-bg-surface);\n  cursor: pointer;\n  text-align: left;\n  box-sizing: border-box;\n}\n.recommendedApps__partnerItem:hover {\n  background: var(--p-color-bg-surface-hover);\n}\n.recommendedApps__dashboardItem {\n  width: 100%;\n  min-width: 0;\n  height: 100%;\n  min-height: 132px;\n  display: flex;\n  gap: var(--p-space-400);\n  align-items: flex-start;\n  padding: var(--p-space-400);\n  border: var(--p-border-width-025) solid var(--p-color-border-secondary);\n  border-radius: var(--p-border-radius-300);\n  background: var(--p-color-bg-surface);\n  box-sizing: border-box;\n}\n.recommendedApps__dashboardContent {\n  flex: 1;\n  min-width: 0;\n  align-self: stretch;\n  display: flex;\n  flex-direction: column;\n  gap: var(--p-space-200);\n}\n.recommendedApps__dashboardHeading {\n  display: flex;\n  align-items: center;\n  gap: var(--p-space-200);\n  min-width: 0;\n}\n.recommendedApps__dashboardTitle {\n  flex: 0 1 auto;\n  min-width: 0;\n  overflow: hidden;\n}\n.recommendedApps__dashboardBadge {\n  display: inline-flex;\n  flex: 0 0 auto;\n  white-space: nowrap;\n}\n.recommendedApps__dashboardActions {\n  margin-top: auto;\n  padding-top: var(--p-space-200);\n}\n.recommendedApps__partnerItem {\n  min-height: 150px;\n}\n.recommendedApps__skeletonItem {\n  width: 100%;\n  min-width: 0;\n  min-height: 132px;\n  display: flex;\n  gap: var(--p-space-400);\n  align-items: flex-start;\n  padding: var(--p-space-400);\n  border: var(--p-border-width-025) solid var(--p-color-border-secondary);\n  border-radius: var(--p-border-radius-300);\n  box-sizing: border-box;\n}\n.recommendedApps__skeletonLogo {\n  width: 64px;\n  height: 64px;\n  flex: 0 0 64px;\n  border-radius: var(--p-border-radius-300);\n  background: var(--p-color-bg-fill-tertiary);\n}\n.recommendedApps__skeletonAction {\n  width: 96px;\n}\n.recommendedApps__itemContent {\n  flex: 1;\n  min-width: 0;\n}\n.recommendedApps__title {\n  min-width: 0;\n}\n.recommendedApps__title > * {\n  display: -webkit-box;\n  overflow: hidden;\n  overflow-wrap: anywhere;\n  -webkit-box-orient: vertical;\n  -webkit-line-clamp: 2;\n}\n.recommendedApps__banner {\n  width: 100%;\n  max-height: 120px;\n  object-fit: cover;\n  border-radius: var(--p-border-radius-200);\n}\n.recommendedApps__ctaText {\n  display: inline-block;\n  margin-top: var(--p-space-100);\n  color: var(--p-color-text-link);\n}\n.recommendedApps__discountBadge {\n  display: inline-flex;\n  align-self: flex-start;\n  width: fit-content;\n  max-width: 100%;\n}\n.recommendedApps__iconButton {\n  border: 0;\n  background: transparent;\n  color: var(--p-color-text-link);\n  cursor: pointer;\n  font: inherit;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-width: 28px;\n  min-height: 28px;\n  padding: var(--p-space-100);\n}\n.recommendedApps__iconButton:hover {\n  color: var(--p-color-text-link-hover);\n}\n.recommendedApps__closeButton {\n  color: var(--p-color-icon-secondary);\n}\n.recommendedApps__closeButton:hover {\n  color: var(--p-color-icon-secondary-hover);\n}\n.recommendedApps__iconButton:focus-visible {\n  outline: var(--p-border-width-050) solid var(--p-color-border-focus);\n  outline-offset: var(--p-space-050);\n  border-radius: var(--p-border-radius-100);\n}\n.recommendedApps__inlineIcon {\n  width: 16px;\n  height: 16px;\n  flex: 0 0 16px;\n  color: currentColor;\n  fill: none;\n  stroke: currentColor;\n}\n.recommendedApps__inlineIcon * {\n  fill: none;\n  stroke: currentColor;\n}\n.recommendedApps__externalLinkIcon {\n  position: absolute;\n  top: var(--p-space-500);\n  right: var(--p-space-500);\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  color: var(--p-color-text-secondary);\n  pointer-events: none;\n}\n.recommendedApps__externalLinkIcon svg,\n.recommendedApps__externalLinkIcon svg * {\n  fill: none;\n  stroke: currentColor;\n}\n.recommendedApps__partnersPage {\n  width: 100%;\n  max-width: 998px;\n  margin: 0 auto;\n  padding: var(--p-space-600) var(--p-space-800);\n  box-sizing: border-box;\n}\n@media (max-width: 767px) {\n  .recommendedApps__partnersPage {\n    padding: var(--p-space-400);\n  }\n  .recommendedApps__partnerItem {\n    padding: var(--p-space-400);\n    padding-right: calc(var(--p-space-400) + 24px);\n  }\n  .recommendedApps__externalLinkIcon {\n    top: var(--p-space-400);\n    right: var(--p-space-400);\n  }\n}\n");

// src/recommended-apps/components/RecommendedAppCard.tsx
import { jsx as jsx15, jsxs as jsxs14 } from "react/jsx-runtime";
var AppLogo = ({ app }) => app.logoUrl ? /* @__PURE__ */ jsx15(
  "img",
  {
    className: "recommendedApps__logo",
    src: app.logoUrl,
    alt: app.logoAlt
  }
) : /* @__PURE__ */ jsx15("div", { className: "recommendedApps__logoPlaceholder", "aria-hidden": "true", children: app.title.trim().charAt(0).toUpperCase() });
var DashboardAppCard = ({
  app,
  position,
  onClick
}) => {
  const handleClick = () => onClick(app, position);
  return /* @__PURE__ */ jsxs14("div", { className: "recommendedApps__dashboardItem", children: [
    /* @__PURE__ */ jsx15(AppLogo, { app }),
    /* @__PURE__ */ jsxs14("div", { className: "recommendedApps__dashboardContent", children: [
      /* @__PURE__ */ jsxs14("div", { className: "recommendedApps__dashboardHeading", children: [
        /* @__PURE__ */ jsx15("div", { className: "recommendedApps__dashboardTitle", title: app.title, children: /* @__PURE__ */ jsx15(Text8, { as: "h3", variant: "headingSm", truncate: true, children: app.title }) }),
        app.discount ? /* @__PURE__ */ jsx15("span", { className: "recommendedApps__dashboardBadge", children: /* @__PURE__ */ jsx15(Badge2, { tone: "success", children: app.discount }) }) : null
      ] }),
      app.imageUrl ? /* @__PURE__ */ jsx15(
        "img",
        {
          className: "recommendedApps__banner",
          src: app.imageUrl,
          alt: app.imageAlt
        }
      ) : null,
      /* @__PURE__ */ jsx15(Text8, { as: "p", variant: "bodyMd", children: app.description }),
      /* @__PURE__ */ jsx15("div", { className: "recommendedApps__dashboardActions", children: /* @__PURE__ */ jsx15(
        Button4,
        {
          onClick: handleClick,
          icon: /* @__PURE__ */ jsx15(
            Download,
            {
              className: "recommendedApps__inlineIcon",
              size: 16,
              strokeWidth: 2
            }
          ),
          children: app.ctaText
        }
      ) })
    ] })
  ] });
};
var RecommendedAppCard = ({
  app,
  position,
  variant,
  onClick
}) => {
  if (variant === "dashboard") {
    return /* @__PURE__ */ jsx15(DashboardAppCard, { app, position, onClick });
  }
  return /* @__PURE__ */ jsxs14(
    "button",
    {
      type: "button",
      className: "recommendedApps__partnerItem",
      onClick: () => onClick(app, position),
      children: [
        /* @__PURE__ */ jsx15(AppLogo, { app }),
        /* @__PURE__ */ jsx15("div", { className: "recommendedApps__itemContent", children: /* @__PURE__ */ jsxs14(BlockStack6, { gap: "100", children: [
          /* @__PURE__ */ jsx15("div", { className: "recommendedApps__title", title: app.title, children: /* @__PURE__ */ jsx15(Text8, { as: "h3", variant: "headingSm", children: app.title }) }),
          app.discount ? /* @__PURE__ */ jsx15("span", { className: "recommendedApps__discountBadge", children: /* @__PURE__ */ jsx15(Badge2, { tone: "success", children: app.discount }) }) : null,
          app.imageUrl ? /* @__PURE__ */ jsx15(
            "img",
            {
              className: "recommendedApps__banner",
              src: app.imageUrl,
              alt: app.imageAlt
            }
          ) : null,
          /* @__PURE__ */ jsx15(Text8, { as: "p", variant: "bodyMd", tone: "subdued", children: app.description }),
          /* @__PURE__ */ jsx15("span", { className: "recommendedApps__ctaText", children: app.ctaText })
        ] }) }),
        /* @__PURE__ */ jsx15("div", { className: "recommendedApps__externalLinkIcon", "aria-hidden": "true", children: /* @__PURE__ */ jsx15(ExternalLink, { size: 16, strokeWidth: 2 }) })
      ]
    }
  );
};

// src/recommended-apps/components/RecommendedAppsSection.tsx
import {
  Box as Box3,
  Button as Button5,
  Card as Card5,
  InlineGrid as InlineGrid2,
  InlineStack as InlineStack4,
  SkeletonBodyText as SkeletonBodyText2,
  SkeletonDisplayText,
  Text as Text9
} from "@shopify/polaris";
import { Eye, X as X4 } from "lucide-react";
import { useEffect as useEffect7, useLayoutEffect as useLayoutEffect2, useMemo, useRef as useRef3, useState as useState8 } from "react";

// src/recommended-apps/constants/index.ts
var RECOMMENDED_APP_CONTENT_TYPE_ID = "recommendedApp";
var DEFAULT_PREVIEW_COUNT = 4;
var RECOMMENDED_APPS_PAGE_SIZE = 10;

// src/recommended-apps/utils/useRecommendedApps.ts
import { useEffect as useEffect6, useRef as useRef2, useState as useState7 } from "react";

// src/recommended-apps/utils/contentful.ts
var matchesApp6 = (app, linkedApp) => {
  const normalizedAppSlug = normalizeAppIdentifier(app);
  const linkedValues = getStringFieldValues(linkedApp?.fields);
  return linkedValues.some(
    (value) => normalizeAppIdentifier(value) === normalizedAppSlug
  );
};
var getAssetText = (value) => typeof value === "string" ? value.trim() || void 0 : void 0;
var getAssetAlt = (description, title, fallback = "") => getAssetText(description) || getAssetText(title) || fallback;
var normalizeTaxonomy = (entry) => {
  const fields = entry?.fields;
  const label = fields?.label?.trim();
  if (!entry?.sys?.id || !fields || fields.enabled === false || !label) return null;
  return {
    id: entry.sys.id,
    label,
    displayOrder: fields.displayOrder ?? 99
  };
};
var normalizeRecommendedApp = (entry, app) => {
  const fields = entry.fields;
  if (!fields || fields.enabled === false || !matchesApp6(app, fields.app)) return null;
  const title = fields.title?.trim();
  const description = fields.description?.trim();
  const ctaText = fields.ctaText?.trim();
  const redirectLink = fields.redirectLink?.trim();
  if (!title || !description || !ctaText || !redirectLink) return null;
  const logoUrl = toContentfulAssetUrl(fields.logo) ?? null;
  const imageUrl = toContentfulAssetUrl(fields.bannerImage) ?? null;
  return {
    id: entry.sys.id,
    title,
    description,
    logoUrl,
    logoAlt: getAssetAlt(
      fields.logo?.fields?.description,
      fields.logo?.fields?.title,
      `${title} logo`
    ),
    imageUrl,
    imageAlt: getAssetAlt(
      fields.bannerImage?.fields?.description,
      fields.bannerImage?.fields?.title,
      title
    ),
    ctaText,
    redirectLink,
    displayOrder: fields.displayOrder ?? 99,
    discount: fields.discount?.trim() || null,
    type: normalizeTaxonomy(fields.type),
    category: normalizeTaxonomy(fields.category)
  };
};
var normalizeRecommendedApps = (collection, app) => {
  if (!collection?.items.length) return [];
  return collection.items.map((entry) => normalizeRecommendedApp(entry, app)).filter((entry) => Boolean(entry)).sort((left, right) => left.displayOrder - right.displayOrder);
};
var getActiveRecommendedApps = async ({
  app,
  getRecommendedApps
}) => {
  try {
    const collection = await getRecommendedApps();
    return normalizeRecommendedApps(collection, app);
  } catch (error) {
    console.error("[Recommended Apps] Failed to fetch Contentful entries", error);
    return [];
  }
};
var filterRecommendedApps = (apps, query, typeId, categoryId) => {
  const normalizedQuery = query.trim().toLowerCase();
  return apps.filter((app) => {
    const matchesQuery = normalizedQuery.length === 0 || [
      app.title,
      app.description,
      app.discount ?? "",
      app.type?.label ?? "",
      app.category?.label ?? ""
    ].join(" ").toLowerCase().includes(normalizedQuery);
    const matchesType = typeId === "all" || app.type?.id === typeId;
    const matchesCategory = categoryId === "all" || app.category?.id === categoryId;
    return matchesQuery && matchesType && matchesCategory;
  });
};
var buildRecommendedAppTaxonomyOptions = (apps, taxonomy, allLabel) => {
  const optionMap = /* @__PURE__ */ new Map();
  for (const app of apps) {
    const item = app[taxonomy];
    if (!item) continue;
    optionMap.set(item.id, {
      label: item.label,
      value: item.id,
      order: item.displayOrder
    });
  }
  return [
    { label: allLabel, value: "all" },
    ...Array.from(optionMap.values()).sort((left, right) => {
      if (left.order !== right.order) return left.order - right.order;
      return left.label.localeCompare(right.label);
    })
  ];
};

// src/recommended-apps/utils/useRecommendedApps.ts
var useRecommendedApps = ({
  app,
  getRecommendedApps
}) => {
  const [apps, setApps] = useState7([]);
  const [loading, setLoading] = useState7(true);
  const isInitialLoadRef = useRef2(true);
  useEffect6(() => {
    let cancelled = false;
    const loadRecommendedApps = async () => {
      if (isInitialLoadRef.current) {
        setLoading(true);
      }
      try {
        const entries = await getActiveRecommendedApps({ app, getRecommendedApps });
        if (!cancelled) setApps(entries);
      } finally {
        if (!cancelled) {
          setLoading(false);
          isInitialLoadRef.current = false;
        }
      }
    };
    void loadRecommendedApps();
    return () => {
      cancelled = true;
    };
  }, [app, getRecommendedApps]);
  return { apps, loading };
};

// src/recommended-apps/components/RecommendedAppsSection.tsx
import { jsx as jsx16, jsxs as jsxs15 } from "react/jsx-runtime";
var getAppsCountStorageKey = (app) => `recommended_apps_count_${app}`;
var getSectionHeightStorageKey = (app) => `recommended_apps_height_${app}`;
var readCachedAppsCount = (app) => {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(getAppsCountStorageKey(app));
    if (saved === null) return null;
    const parsed = Number(saved);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  } catch {
    return null;
  }
};
var writeCachedAppsCount = (app, count) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(getAppsCountStorageKey(app), String(count));
  } catch {
  }
};
var readCachedSectionHeight = (app, width) => {
  if (typeof window === "undefined" || !width) return null;
  try {
    const saved = localStorage.getItem(getSectionHeightStorageKey(app));
    if (saved === null) return null;
    const parsed = JSON.parse(saved);
    if (typeof parsed?.width !== "number" || typeof parsed?.height !== "number")
      return null;
    return Math.abs(parsed.width - width) <= 1 ? parsed.height : null;
  } catch {
    return null;
  }
};
var writeCachedSectionHeight = (app, size) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(getSectionHeightStorageKey(app), JSON.stringify(size));
  } catch {
  }
};
var RecommendedAppsSectionSkeleton = ({
  itemsCount
}) => /* @__PURE__ */ jsxs15(Card5, { roundedAbove: "xs", padding: "0", children: [
  /* @__PURE__ */ jsx16(
    Box3,
    {
      paddingBlock: "300",
      paddingInline: "400",
      borderBlockEndWidth: "025",
      borderColor: "border",
      children: /* @__PURE__ */ jsx16(SkeletonDisplayText, { size: "small" })
    }
  ),
  /* @__PURE__ */ jsx16(Box3, { padding: "500", children: /* @__PURE__ */ jsx16(InlineGrid2, { columns: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }, gap: "300", children: Array.from({ length: itemsCount }, (_, index) => /* @__PURE__ */ jsxs15(
    "div",
    {
      className: "recommendedApps__skeletonItem",
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx16("div", { className: "recommendedApps__skeletonLogo" }),
        /* @__PURE__ */ jsx16("div", { className: "recommendedApps__itemContent", children: /* @__PURE__ */ jsx16(SkeletonBodyText2, { lines: 3 }) })
      ]
    },
    index
  )) }) }),
  /* @__PURE__ */ jsx16(
    Box3,
    {
      paddingBlock: "300",
      paddingInline: "400",
      background: "bg-surface-secondary",
      borderBlockStartWidth: "025",
      borderColor: "border",
      children: /* @__PURE__ */ jsx16("div", { className: "recommendedApps__skeletonAction", children: /* @__PURE__ */ jsx16(SkeletonBodyText2, { lines: 1 }) })
    }
  )
] });
var RecommendedAppsSection = ({
  app,
  getRecommendedApps,
  onAppClick,
  onViewMore,
  onSectionViewed,
  previewCount = DEFAULT_PREVIEW_COUNT,
  title = "Recommended apps",
  viewMoreLabel = "View more",
  footerText = "Discover apps from trusted Shopify partners.",
  reserveSpaceWhileLoading = false
}) => {
  const { apps, loading } = useRecommendedApps({ app, getRecommendedApps });
  const [cachedAppsCount] = useState8(() => readCachedAppsCount(app));
  const [showRecommended, setShowRecommended] = useState8(() => {
    if (typeof window === "undefined") {
      return true;
    }
    try {
      const saved = sessionStorage.getItem("recommended_app_closed");
      return saved !== "true";
    } catch {
      return true;
    }
  });
  const previewApps = useMemo(
    () => apps.slice(0, previewCount),
    [apps, previewCount]
  );
  const hasTrackedSectionViewRef = useRef3(false);
  const sectionRef = useRef3(null);
  const [skeletonMinHeight, setSkeletonMinHeight] = useState8(
    null
  );
  const handleClose = () => {
    setShowRecommended(false);
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem("recommended_app_closed", "true");
    } catch {
    }
  };
  useEffect7(() => {
    if (loading || previewApps.length === 0 || hasTrackedSectionViewRef.current)
      return;
    hasTrackedSectionViewRef.current = true;
    onSectionViewed?.({
      appsCount: apps.length,
      previewCount: previewApps.length
    });
  }, [apps.length, loading, onSectionViewed, previewApps.length]);
  useEffect7(() => {
    if (!reserveSpaceWhileLoading || loading) return;
    writeCachedAppsCount(app, previewApps.length);
  }, [app, loading, previewApps.length, reserveSpaceWhileLoading]);
  useLayoutEffect2(() => {
    if (!reserveSpaceWhileLoading || !loading) return;
    const width = sectionRef.current?.getBoundingClientRect().width ?? 0;
    setSkeletonMinHeight(readCachedSectionHeight(app, width));
  }, [app, loading, reserveSpaceWhileLoading]);
  useEffect7(() => {
    const section2 = sectionRef.current;
    if (!reserveSpaceWhileLoading || loading || !section2) return;
    const store = () => {
      const { width, height } = section2.getBoundingClientRect();
      if (width && height) writeCachedSectionHeight(app, { width, height });
    };
    store();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(store);
    observer.observe(section2);
    return () => observer.disconnect();
  }, [app, loading, reserveSpaceWhileLoading]);
  if (!showRecommended) {
    return null;
  }
  if (loading) {
    const skeletonItemsCount = cachedAppsCount ?? previewCount;
    if (!reserveSpaceWhileLoading || skeletonItemsCount === 0) return null;
    return /* @__PURE__ */ jsx16(
      "div",
      {
        ref: sectionRef,
        style: skeletonMinHeight ? { minHeight: skeletonMinHeight } : void 0,
        children: /* @__PURE__ */ jsx16(
          RecommendedAppsSectionSkeleton,
          {
            itemsCount: Math.min(skeletonItemsCount, previewCount)
          }
        )
      }
    );
  }
  if (previewApps.length === 0) return null;
  const section = /* @__PURE__ */ jsxs15(Card5, { roundedAbove: "xs", padding: "0", children: [
    /* @__PURE__ */ jsx16(
      Box3,
      {
        paddingBlock: "300",
        paddingInline: "400",
        borderBlockEndWidth: "025",
        borderColor: "border",
        children: /* @__PURE__ */ jsxs15(InlineStack4, { align: "space-between", blockAlign: "center", gap: "300", children: [
          /* @__PURE__ */ jsx16(Text9, { as: "h2", variant: "headingMd", children: title }),
          /* @__PURE__ */ jsx16(
            "button",
            {
              type: "button",
              className: "recommendedApps__iconButton recommendedApps__closeButton",
              onClick: handleClose,
              "aria-label": "Close recommended apps",
              children: /* @__PURE__ */ jsx16(
                X4,
                {
                  className: "recommendedApps__inlineIcon",
                  size: 16,
                  strokeWidth: 2
                }
              )
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx16(Box3, { padding: "500", children: /* @__PURE__ */ jsx16(InlineGrid2, { columns: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }, gap: "300", children: previewApps.map((recommendedApp, index) => /* @__PURE__ */ jsx16(
      RecommendedAppCard,
      {
        app: recommendedApp,
        position: index + 1,
        variant: "dashboard",
        onClick: (selectedApp, position) => onAppClick(selectedApp, position, "dashboard")
      },
      recommendedApp.id
    )) }) }),
    /* @__PURE__ */ jsx16(
      Box3,
      {
        paddingBlock: "300",
        paddingInline: "400",
        background: "bg-surface-secondary",
        borderBlockStartWidth: "025",
        borderColor: "border",
        children: /* @__PURE__ */ jsxs15(InlineStack4, { align: "space-between", blockAlign: "center", gap: "300", children: [
          /* @__PURE__ */ jsx16(Text9, { as: "p", variant: "bodyMd", tone: "subdued", children: footerText }),
          /* @__PURE__ */ jsx16(
            Button5,
            {
              onClick: onViewMore,
              icon: /* @__PURE__ */ jsx16(
                Eye,
                {
                  className: "recommendedApps__inlineIcon",
                  size: 16,
                  strokeWidth: 2
                }
              ),
              children: viewMoreLabel
            }
          )
        ] })
      }
    )
  ] });
  return reserveSpaceWhileLoading ? /* @__PURE__ */ jsx16("div", { ref: sectionRef, children: section }) : section;
};

// src/recommended-apps/components/RecommendedAppsPage.tsx
import {
  BlockStack as BlockStack7,
  Card as Card6,
  InlineGrid as InlineGrid3,
  InlineStack as InlineStack5,
  Page,
  Pagination,
  Select,
  Text as Text10,
  TextField
} from "@shopify/polaris";
import { ArrowLeft, Search } from "lucide-react";
import { useCallback as useCallback2, useEffect as useEffect8, useMemo as useMemo2, useRef as useRef4, useState as useState9 } from "react";

// src/recommended-apps/utils/buildResultsContext.ts
var buildRecommendedAppsResultsContext = ({
  apps,
  query,
  typeId,
  categoryId,
  filteredApps,
  currentPage,
  totalPages
}) => {
  const trimmedQuery = query.trim();
  return {
    appsCount: apps.length,
    resultsCount: filteredApps.length,
    searchQueryLength: trimmedQuery.length,
    hasQuery: trimmedQuery.length > 0,
    filterType: typeId,
    filterCategory: categoryId,
    page: currentPage,
    totalPages
  };
};
var hasActivePartnersFilters = (query, typeId, categoryId) => {
  return query.trim().length > 0 || typeId !== "all" || categoryId !== "all";
};

// src/recommended-apps/components/RecommendedAppsPage.tsx
import { jsx as jsx17, jsxs as jsxs16 } from "react/jsx-runtime";
var SEARCH_DEBOUNCE_MS = 500;
var searchIcon = /* @__PURE__ */ jsx17(Search, { className: "recommendedApps__inlineIcon", size: 16, strokeWidth: 2 });
var RecommendedAppsPage = ({
  app,
  getRecommendedApps,
  onAppClick,
  onBack,
  onPageViewed,
  onSearchChange,
  onFilterChange,
  onPaginationChange,
  onEmptyResults,
  pageTitle = "Our partners",
  searchPlaceholder = "Search",
  searchLabel = "Search partners",
  typeFilterLabel = "Type",
  categoryFilterLabel = "Category",
  loadingMessage = "Loading partners...",
  emptyMessage = "No partners match your filters.",
  backAccessibilityLabel = "Back to dashboard"
}) => {
  const { apps, loading } = useRecommendedApps({ app, getRecommendedApps });
  const [query, setQuery] = useState9("");
  const [typeId, setTypeId] = useState9("all");
  const [categoryId, setCategoryId] = useState9("all");
  const [page, setPage] = useState9(1);
  const hasTrackedPageViewRef = useRef4(false);
  const lastTrackedSearchLengthRef = useRef4(null);
  const hadEmptyResultsRef = useRef4(false);
  const typeOptions = useMemo2(
    () => buildRecommendedAppTaxonomyOptions(apps, "type", typeFilterLabel),
    [apps, typeFilterLabel]
  );
  const categoryOptions = useMemo2(
    () => buildRecommendedAppTaxonomyOptions(apps, "category", categoryFilterLabel),
    [apps, categoryFilterLabel]
  );
  const filteredApps = useMemo2(
    () => filterRecommendedApps(apps, query, typeId, categoryId),
    [apps, categoryId, query, typeId]
  );
  const totalPages = Math.max(
    1,
    Math.ceil(filteredApps.length / RECOMMENDED_APPS_PAGE_SIZE)
  );
  const currentPage = Math.min(page, totalPages);
  const visibleApps = filteredApps.slice(
    (currentPage - 1) * RECOMMENDED_APPS_PAGE_SIZE,
    currentPage * RECOMMENDED_APPS_PAGE_SIZE
  );
  const buildResultsContext = useCallback2(
    (pageOverride) => buildRecommendedAppsResultsContext({
      apps,
      query,
      typeId,
      categoryId,
      filteredApps,
      currentPage: pageOverride ?? currentPage,
      totalPages
    }),
    [apps, categoryId, currentPage, filteredApps, query, totalPages, typeId]
  );
  useEffect8(() => {
    setPage(1);
  }, [query, typeId, categoryId]);
  useEffect8(() => {
    if (loading || hasTrackedPageViewRef.current) return;
    hasTrackedPageViewRef.current = true;
    onPageViewed?.(buildResultsContext(1));
  }, [buildResultsContext, loading, onPageViewed]);
  useEffect8(() => {
    if (!onSearchChange || loading) return;
    const trimmedLength = query.trim().length;
    const timeoutId = window.setTimeout(() => {
      if (lastTrackedSearchLengthRef.current === trimmedLength) return;
      lastTrackedSearchLengthRef.current = trimmedLength;
      onSearchChange(buildResultsContext());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [buildResultsContext, loading, onSearchChange, query]);
  useEffect8(() => {
    if (!onEmptyResults || loading) return;
    const hasActiveFilters = hasActivePartnersFilters(query, typeId, categoryId);
    const isEmpty = filteredApps.length === 0;
    if (isEmpty && hasActiveFilters && !hadEmptyResultsRef.current) {
      hadEmptyResultsRef.current = true;
      onEmptyResults(buildResultsContext());
      return;
    }
    if (!isEmpty || !hasActiveFilters) {
      hadEmptyResultsRef.current = false;
    }
  }, [
    buildResultsContext,
    categoryId,
    filteredApps.length,
    loading,
    onEmptyResults,
    query,
    typeId
  ]);
  const handleFilterChange = useCallback2(
    (filterKind, value) => {
      const nextTypeId = filterKind === "type" ? value : typeId;
      const nextCategoryId = filterKind === "category" ? value : categoryId;
      if (filterKind === "type") {
        setTypeId(value);
      } else {
        setCategoryId(value);
      }
      if (!onFilterChange) return;
      const nextFiltered = filterRecommendedApps(
        apps,
        query,
        nextTypeId,
        nextCategoryId
      );
      const nextTotalPages = Math.max(
        1,
        Math.ceil(nextFiltered.length / RECOMMENDED_APPS_PAGE_SIZE)
      );
      onFilterChange(
        filterKind,
        value,
        buildRecommendedAppsResultsContext({
          apps,
          query,
          typeId: nextTypeId,
          categoryId: nextCategoryId,
          filteredApps: nextFiltered,
          currentPage: 1,
          totalPages: nextTotalPages
        })
      );
    },
    [apps, categoryId, onFilterChange, query, typeId]
  );
  const handlePaginationChange = (nextPage) => {
    setPage(nextPage);
    onPaginationChange?.(
      buildRecommendedAppsResultsContext({
        apps,
        query,
        typeId,
        categoryId,
        filteredApps,
        currentPage: nextPage,
        totalPages
      })
    );
  };
  return /* @__PURE__ */ jsx17(Page, { children: /* @__PURE__ */ jsx17("div", { className: "recommendedApps__partnersPage", children: /* @__PURE__ */ jsxs16(BlockStack7, { gap: "500", children: [
    /* @__PURE__ */ jsxs16(InlineStack5, { gap: "300", blockAlign: "center", children: [
      /* @__PURE__ */ jsx17(
        "button",
        {
          type: "button",
          className: "recommendedApps__iconButton",
          "aria-label": backAccessibilityLabel,
          onClick: onBack,
          children: /* @__PURE__ */ jsx17(
            ArrowLeft,
            {
              className: "recommendedApps__inlineIcon",
              size: 16,
              strokeWidth: 2
            }
          )
        }
      ),
      /* @__PURE__ */ jsx17(Text10, { as: "h1", variant: "headingLg", children: pageTitle })
    ] }),
    /* @__PURE__ */ jsx17(Card6, { roundedAbove: "xs", children: /* @__PURE__ */ jsxs16(BlockStack7, { gap: "400", children: [
      /* @__PURE__ */ jsxs16(
        InlineGrid3,
        {
          columns: {
            xs: 1,
            sm: 1,
            md: "1fr auto auto",
            lg: "1fr auto auto"
          },
          gap: "300",
          children: [
            /* @__PURE__ */ jsx17(
              TextField,
              {
                label: searchLabel,
                labelHidden: true,
                value: query,
                onChange: setQuery,
                autoComplete: "off",
                placeholder: searchPlaceholder,
                prefix: searchIcon,
                clearButton: true,
                onClearButtonClick: () => setQuery("")
              }
            ),
            /* @__PURE__ */ jsx17(
              Select,
              {
                label: typeFilterLabel,
                labelHidden: true,
                options: typeOptions,
                value: typeId,
                onChange: (value) => handleFilterChange("type", value),
                disabled: typeOptions.length === 1
              }
            ),
            /* @__PURE__ */ jsx17(
              Select,
              {
                label: categoryFilterLabel,
                labelHidden: true,
                options: categoryOptions,
                value: categoryId,
                onChange: (value) => handleFilterChange("category", value),
                disabled: categoryOptions.length === 1
              }
            )
          ]
        }
      ),
      loading ? /* @__PURE__ */ jsx17(Text10, { as: "p", tone: "subdued", children: loadingMessage }) : visibleApps.length > 0 ? /* @__PURE__ */ jsx17(
        InlineGrid3,
        {
          columns: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 },
          gap: "400",
          children: visibleApps.map((recommendedApp, index) => /* @__PURE__ */ jsx17(
            RecommendedAppCard,
            {
              app: recommendedApp,
              position: (currentPage - 1) * RECOMMENDED_APPS_PAGE_SIZE + index + 1,
              variant: "list",
              onClick: (selectedApp, position) => onAppClick(selectedApp, position, "partners")
            },
            recommendedApp.id
          ))
        }
      ) : /* @__PURE__ */ jsx17(Text10, { as: "p", tone: "subdued", children: emptyMessage }),
      filteredApps.length > RECOMMENDED_APPS_PAGE_SIZE ? /* @__PURE__ */ jsx17(InlineStack5, { align: "center", children: /* @__PURE__ */ jsx17(
        Pagination,
        {
          hasPrevious: currentPage > 1,
          onPrevious: () => handlePaginationChange(currentPage - 1),
          hasNext: currentPage < totalPages,
          onNext: () => handlePaginationChange(currentPage + 1)
        }
      ) }) : null
    ] }) })
  ] }) }) });
};
export {
  Banner,
  BannerCountry,
  BannerLocation,
  DEFAULT_PREVIEW_COUNT,
  FORDEER_APPS,
  FORDEER_APP_SECTIONS,
  FaqAccordion,
  FeatureGuide,
  FeatureGuideModal,
  FeatureGuideTriggerButton,
  FordeerApp,
  Marquee,
  MerchantLogosMarquee,
  PricingSocialProof,
  PricingTrustMetrics,
  RECOMMENDED_APPS_PAGE_SIZE,
  RECOMMENDED_APP_CONTENT_TYPE_ID,
  RecommendedAppCard,
  RecommendedAppsPage,
  RecommendedAppsSection,
  TestimonialsMarquee,
  TutorialLocation,
  TutorialSection,
  WhatsNewContent,
  WhatsNewModal,
  WhatsNewTeaser,
  WhatsNewType,
  buildRecommendedAppTaxonomyOptions,
  buildRecommendedAppsResultsContext,
  filterFeatureGuideVideos,
  filterRecommendedApps,
  getActiveBanner,
  getActiveBanners,
  getActiveFaqItems,
  getActiveFeatureGuide,
  getActivePricingSocialProof,
  getActivePricingTrustMetrics,
  getActiveRecommendedApps,
  getActiveTutorialSection,
  getActiveWhatsNewSections,
  getActiveWhatsNewTeasers,
  getWhatsNewChangelogUrl,
  getYoutubeEmbedUrl,
  hasActivePartnersFilters,
  normalizeBannerData,
  normalizeFeatureGuideVideoData,
  normalizeRecommendedApp,
  normalizeTutorialSectionData,
  normalizeTutorialSlideData,
  useRecommendedApps
};
